<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, onUpdated, ref, watch } from "vue";
import type { AdminCorrectionRequest, AdminDevice, AdminDeviceHealth, AdminDeviceRecord, AdminDeviceSyncHistoryPage, AdminDeviceUpdate, AdminGranularity, AdminIdentityAuditRow, AdminMetrics, AdminOrderFilters, AdminStoreHealth, AdminSyncHistoryRow, AdminTheme, AdminTrendPoint, AdminUser, AdminUserCreate, AdminUserUpdate, StoreProfile } from "@shared/admin";
import type { AccountProfile, AccountSecurityEvent } from "@shared/account";
import { hasPermission, type AuthSession, type BootstrapStoreOption } from "@shared/identity";
import type { ConflictResolutionAction, MenuItem, Order, OrderEvent, PaymentMethod, ServerSyncConflict } from "@shared/contracts";
import type { MenuCategory, MenuRevision, MenuRevisionInput, MenuSyncStatusResponse } from "@shared/menu";
import { applyRemoteOrderCorrection, createRemoteUser, deleteRemoteDevice, deleteRemoteUser, fetchRemoteAdminData, fetchRemoteAdminOrderDetail, fetchRemoteDeviceSyncHistory, fetchRemoteDevices, fetchRemoteIdentityAudit, fetchRemoteOrdersCsv, fetchRemoteOrdersExcel, fetchRemoteStoreHealth, fetchRemoteStoreProfile, fetchRemoteSyncConflicts, fetchRemoteUsers, resolveRemoteSyncConflict, updateRemoteDevice, updateRemoteStoreProfile, updateRemoteUser } from "./lib/admin-api";
import { AuthClient } from "./lib/auth-client";
import { languageTag, PWA_UPDATE_EVENT, requestPwaUpdate } from "./lib/pwa";
import { readLocalStorage, writeLocalStorage } from "./lib/storage";
import { translate, translateDocument } from "./lib/i18n";
import { displayOrderNumber, orderLineAmount } from "./lib/admin-order-display";
import { createAdminAppCore } from "./lib/admin-app-core";
import { createMenuDraft, fetchMenuRevisions, fetchMenuSyncStatus, fetchPublishedMenu, publishMenuRevision, rollbackMenuRevision } from "./lib/menu-api";
import { adminDatePickerClearPreset, adminPresetRange, type AdminDatePreset } from "./lib/admin-date";
import { minutesToPosTime, posDateBoundaryIso, posDateRangeAdapter, posTimeToMinutes, posTimeAdapter, type PosDateRangeValue, type PosTimeValue } from "./lib/date-picker";
import { BaseAccordion, BaseAlert, BaseAutocomplete, BaseBreadcrumb, BaseButton, BaseDialog, BaseDrawer, BaseHoverLabel, BaseIcon, BaseInput, BasePagination, BasePopoverButton, BaseSelect, BaseTable, BaseTag, BaseTooltip, ConfirmationDialog, useConfirmDialog, type BaseSelectOption, type BreadcrumbItem, type PaginationChangeEvent } from "@lunagoai/vue-ui";
import type { TableColumn } from "@lunagoai/vue-ui";
import { createCurrencyOptions, createRegionOptions } from "@lunagoai/locale-data";
import { createTimezoneOptions, type TimezoneOption } from "@lunagoai/timezone";
import { AdminResourceController, adminResourceCache, adminResourceKey, type AdminResourceScope } from "./lib/admin-resource-cache";
import AppLoadingOverlay from "./components/AppLoadingOverlay.vue";
import PosSelect from "./components/PosSelect.vue";
import PwaUpdateDialog from "./components/PwaUpdateDialog.vue";
import BaseDatePicker from "@lunagoai/vue-ui/components/BaseDatePicker.vue";
import BaseTrendChart, { type BaseTrendChartAxis, type BaseTrendChartPoint, type BaseTrendChartSeries } from "@lunagoai/vue-ui/components/BaseTrendChart.vue";
import AdminMenuManagement from "./components/AdminMenuManagement.vue";
import AdminUsersView from "./components/AdminUsersView.vue";
import AdminDevicesView from "./components/AdminDevicesView.vue";
import AdminShell, { type AdminShellNavGroup } from "./components/AdminShell.vue";
import PlatformConsole from "./components/PlatformConsole.vue";
import AccountView from "./components/AccountView.vue";

type AdminView = "dashboard" | "orders" | "menu" | "menu-revisions" | "store-profile" | "store-profile-history" | "management" | "devices" | "device-history" | "conflicts" | "account" | "account-security";
type AdminOrderTableRow = Record<string, unknown> & {
  id: string;
  orderNo: string;
  createdAt: string;
  actorId: string;
  paymentMethod: PaymentMethod;
  totalMinor: number;
  status: Order["status"];
  sourceOrder: Order;
};
type AdminDataResource = NonNullable<Awaited<ReturnType<typeof fetchRemoteAdminData>>>;
type AdminMenuResource = { revisions: MenuRevision[]; syncStatus: MenuSyncStatusResponse | null; published: { items: MenuItem[]; categories: MenuCategory[] } | null };
type AdminDeviceHistoryResource = AdminDeviceSyncHistoryPage;

const adminViewPaths: Record<AdminView, string> = {
  dashboard: "/store/dashboard",
  orders: "/store/orders",
  menu: "/store/menu",
  "menu-revisions": "/store/menu/versions",
  "store-profile": "/store/store-profile",
  "store-profile-history": "/store/store-profile/history",
  management: "/store/staff",
  devices: "/store/devices",
  conflicts: "/store/orders/conflicts",
  account: "/store/account",
  "account-security": "/store/account/security",
};
const adminPathViews = Object.entries(adminViewPaths).reduce<Record<string, AdminView>>((result, [viewId, path]) => {
  result[path] = viewId as AdminView;
  return result;
}, {});
type AdminRoute = { view: AdminView; entityId?: string };
function routeFromPath(pathname: string): AdminRoute {
  if (pathname === "/store") return { view: "dashboard" };
  const deviceHistory = pathname.match(/^\/store\/devices\/([^/]+)\/history$/);
  if (deviceHistory) return { view: "device-history", entityId: decodeURIComponent(deviceHistory[1]!) };
  return { view: adminPathViews[pathname] ?? "dashboard" };
}
function adminPathFor(viewId: AdminView, entityId?: string | null): string {
  if (viewId === "device-history" && entityId) return `/store/devices/${encodeURIComponent(entityId)}/history`;
  return adminViewPaths[viewId] ?? "/store/dashboard";
}
const initialRoute = routeFromPath(typeof window === "undefined" ? "/store/dashboard" : window.location.pathname);
const view = ref<AdminView>(initialRoute.view);
const historyEntityId = ref<string | null>(initialRoute.entityId ?? null);
const platformMode = typeof window !== "undefined" && (["/platform", "/platform/dashboard", "/platform/users", "/platform/stores", "/platform/audit", "/platform/account", "/platform/account/security"].includes(window.location.pathname) || /^\/platform\/stores\/[^/]+$/.test(window.location.pathname));
const platformAuditOnly = typeof window !== "undefined" && window.location.pathname === "/platform/audit";
const platformUsersOnly = typeof window !== "undefined" && window.location.pathname === "/platform/users";
const shellLoading = ref(true);
const loading = ref(true);
const dataLoading = ref(false);
const dataLoaded = ref(false);
const busy = ref(false);
const message = ref("");
const errorMessage = ref("");
const bootstrapError = ref("");
const profile = ref<StoreProfile | null>(null);
const storeClock = ref(new Date());
const adminCore = createAdminAppCore();
const { confirm } = useConfirmDialog();
const adminTheme = adminCore.theme.theme;
const filterDrawerTheme = computed<"light" | "dark">(() => adminCore.theme.isDark.value ? "dark" : "light");
const adminLanguage = adminCore.language.currentLanguage;
const orders = ref<Order[]>([]);
const menu = ref<MenuItem[]>([]);
const menuCategories = ref<MenuCategory[]>([]);
const menuRevisions = ref<MenuRevision[]>([]);
const menuSyncStatus = ref<MenuSyncStatusResponse | null>(null);
const menuSavedToken = ref("");
const menuDraftDirty = ref(false);
const menuBusy = ref(false);
const metrics = ref<AdminMetrics>({ grossSalesMinor: 0, netSalesMinor: 0, activeOrderCount: 0, cashSalesMinor: 0, cashOrderCount: 0, cardSalesMinor: 0, cardOrderCount: 0, mixedSalesMinor: 0, mixedOrderCount: 0, refundSalesMinor: 0, refundCount: 0 });
const trend = ref<AdminTrendPoint[]>([]);
const auditRows = ref<Array<{ id: string; orderId?: string; label: string; type: string; occurredAt: string; amountMinor: number; actorId: string; reason?: string }>>([]);
const managementUsers = ref<AdminUser[]>([]);
const managementDevices = ref<AdminDevice[]>([]);
const identityAuditRows = ref<AdminIdentityAuditRow[]>([]);
const deviceHistoryOpen = ref(false);
const deviceHistoryBusy = ref(false);
const deviceHistoryDevice = ref<AdminDevice | null>(null);
const deviceHistoryRows = ref<AdminSyncHistoryRow[]>([]);
const deviceHistorySecurityVisible = ref(false);
const deviceHistoryBootstrapSourceIp = ref<string | undefined>(undefined);
const deviceHistoryBootstrapUserAgent = ref<string | undefined>(undefined);
const deviceHistoryPage = ref(1);
const deviceHistoryPageSize = ref(10);
const deviceHistoryTotal = ref(0);
const syncConflicts = ref<ServerSyncConflict[]>([]);
const online = ref(typeof navigator === "undefined" ? true : navigator.onLine);
const selectedOrder = ref<Order | null>(null);
const orderDialogOpen = ref(false);
const orderPaymentDetailsOpen = ref(false);
const selectedOrderEvents = ref<Array<{ id: string; label: string; occurredAt: string; actorId: string; reason?: string }>>([]);
const orderDetailResource = new AdminResourceController<{ order: Order; events: Array<{ id: string; label: string; occurredAt: string; actorId: string; reason?: string }> }>();
const dataResource = new AdminResourceController<AdminDataResource>();
const profileResource = new AdminResourceController<StoreProfile>();
const menuResource = new AdminResourceController<AdminMenuResource>();
const managementUsersResource = new AdminResourceController<AdminUser[]>();
const managementDevicesResource = new AdminResourceController<AdminDeviceRecord[]>();
const deviceHistoryResource = new AdminResourceController<AdminDeviceHistoryResource>();
const identityAuditResource = new AdminResourceController<AdminIdentityAuditRow[]>();
const conflictsResource = new AdminResourceController<ServerSyncConflict[]>();
const granularity = ref<AdminGranularity>("daily");
const filters = ref<AdminOrderFilters>({ status: "all" });
const orderOffset = ref(0);
const orderLimit = ref(10);
const orderTotal = ref(0);
const orderHasMore = ref(false);
const orderPage = computed({
  get: () => Math.floor(orderOffset.value / orderLimit.value) + 1,
  set: (page: number) => { orderOffset.value = Math.max(0, (page - 1) * orderLimit.value); },
});
const orderSortBy = ref<"createdAt" | "orderNo" | "totalMinor">("createdAt");
const orderSortDirection = ref<"asc" | "desc">("desc");
const fromDate = ref("");
const toDate = ref("");
const dateCalendarCount = ref<"single" | "dual">("dual");
let dateCalendarMedia: MediaQueryList | undefined;
const adminDatePickerLocale = computed(() => adminLanguage.value === "en" ? "en-GB" : languageTag(adminLanguage.value));
const adminDateRangeValue = computed<PosDateRangeValue>(() => [fromDate.value || null, toDate.value || null]);
const filterDrawerOpen = ref(false);
const activeDatePreset = ref<AdminDatePreset | null>(null);
const session = ref<AuthSession | null>(null);
const authBusy = ref(false);
const authError = ref("");
const authForm = ref({ email: readLocalStorage("posnext.admin-email") ?? "", secret: "" });
const authStores = ref<BootstrapStoreOption[]>([]);
const authStoreId = ref(readLocalStorage("posnext.store-id") ?? "");
const authStage = ref<"credentials" | "store">("credentials");
const accountPopoverOpen = ref(false);
const storeSwitchDialogOpen = ref(false);
const pendingStoreId = ref("");
const storeSwitchBusy = ref(false);
const pwaUpdateVisible = ref(false);
const pwaUpdateBusy = ref(false);
const pageReloadBusy = ref(false);
const accountProfile = ref<AccountProfile | null>(null);
const accountSecurityEvents = ref<AccountSecurityEvent[]>([]);
const accountStoreHealth = ref<AdminStoreHealth[]>([]);
const managementDeviceHealth = ref<AdminDeviceHealth[]>([]);
const accountLoading = ref(false);
const accountRefreshing = ref(false);
const accountResource = new AdminResourceController<{ account: AccountProfile; events: AccountSecurityEvent[]; storeHealth: AdminStoreHealth[] }>();
const ADMIN_SESSION_HINT_KEY = "posnext.admin-session-hint";
const ADMIN_BOOTSTRAP_TIMEOUT_MS = 15_000;
const businessDayStartTime = ref<PosTimeValue>(null);
const regionSearch = ref("");
const timezoneSearch = ref("");

// Admin business data is server-authoritative. Browser storage is limited to
// session hints and harmless UI preferences; it is never a business fallback.
const authClient = new AuthClient();
let sessionCheckTimer: number | undefined;
let storeClockTimer: number | undefined;
let dataReloadTimer: number | undefined;
let systemThemeMedia: MediaQueryList | undefined;

const currentResourceScope = computed<AdminResourceScope | null>(() => session.value ? { accountId: session.value.actorId, storeId: session.value.storeId } : null);
function resourceKey(resource: string, query = ""): string | null {
  const scope = currentResourceScope.value;
  return scope ? adminResourceKey(scope, resource, query) : null;
}
function rememberResource<T>(resource: string, value: T, query = ""): void {
  const key = resourceKey(resource, query);
  if (key) adminResourceCache.write(key, value);
}

async function withTimeout<T>(task: Promise<T>, message: string): Promise<T> {
  let timer: number | undefined;
  try {
    return await Promise.race([
      task,
      new Promise<T>((_, reject) => {
        timer = window.setTimeout(() => reject(new Error(message)), ADMIN_BOOTSTRAP_TIMEOUT_MS);
      }),
    ]);
  } finally {
    if (timer !== undefined) window.clearTimeout(timer);
  }
}

function applyTheme(theme: AdminTheme): void {
  adminCore.theme.setTheme(theme);
  syncThemeDom();
}

function syncThemeDom(): void {
  const resolved = adminCore.theme.isDark.value ? "dark" : "light";
  document.documentElement.style.colorScheme = resolved;
}

// Apply the persisted theme before the first render so teleported shared UI
// primitives (including BaseDialog) receive the same theme as the app shell.
applyTheme(adminTheme.value);

function handleSystemThemeChange(): void {
  if (adminTheme.value === "system") {
    adminCore.theme.apply();
    syncThemeDom();
  }
}

async function applyUiLanguage(): Promise<void> {
  await nextTick();
  document.documentElement.lang = languageTag(adminLanguage.value);
  translateDocument(adminLanguage.value);
}
type AdminNavItem = { id: AdminView; label: string; icon: string };

const navItems: AdminNavItem[] = [
  { id: "dashboard", label: "nav.dashboard", icon: "List" },
  { id: "orders", label: "nav.orderHistory", icon: "Inbox" },
  { id: "menu", label: "nav.menuManagement", icon: "FolderTree" },
  { id: "store-profile", label: "ui.storeProfile", icon: "Database" },
  { id: "management", label: "ui.posStaff", icon: "Users" },
  { id: "devices", label: "ui.registeredDevices", icon: "Monitor" },
  { id: "conflicts", label: "ui.orderConflicts", icon: "AlertTriangle" },
  { id: "account", label: "ui.account", icon: "UserCircle" },
];
const dashboardPresets = [
  { id: "today", label: "ui.today" },
  { id: "yesterday", label: "ui.yesterday" },
  { id: "this-week", label: "ui.thisWeek" },
  { id: "this-month", label: "ui.thisMonth" },
  { id: "this-year", label: "ui.thisYear" },
] as const;

const t = (key: string): string => translate(adminLanguage.value, key);
const metricTransition = {
  "enter-active-class": "transition duration-150 ease-out",
  "enter-from-class": "translate-y-1 opacity-0",
  "enter-to-class": "translate-y-0 opacity-100",
  "leave-active-class": "transition duration-100 ease-in",
  "leave-from-class": "translate-y-0 opacity-100",
  "leave-to-class": "-translate-y-1 opacity-0",
};
const paymentOptions = computed<BaseSelectOption[]>(() => [
  { value: "", label: t("ui.all") },
  { value: "cash", label: t("ui.cash") },
  { value: "card", label: t("ui.card") },
  { value: "mixed", label: t("ui.mixed") },
]);
const statusOptions = computed<BaseSelectOption[]>(() => [
  { value: "all", label: t("ui.all") },
  { value: "completed", label: t("ui.completed") },
  { value: "voided", label: t("ui.voided") },
]);
const granularityOptions = computed<BaseSelectOption[]>(() => [
  { value: "hourly", label: t("ui.hourly") },
  { value: "daily", label: t("ui.daily") },
  { value: "weekly", label: t("ui.weekly") },
  { value: "monthly", label: t("ui.monthly") },
]);
const currencyOptions = computed<BaseSelectOption[]>(() => [
  ...createCurrencyOptions(adminLanguage.value === "zh-Hant" ? "zh-Hant" : adminLanguage.value === "zh-Hans" ? "zh-Hans" : "en").map((option) => ({ value: option.code, label: `${option.symbol} ${option.code} · ${option.name}` })),
]);
const weekStartOptions = computed<BaseSelectOption[]>(() => [
  { value: 1, label: t("ui.monday") },
  { value: 0, label: t("ui.sunday") },
]);
const regionOptions = computed(() => createRegionOptions(adminLanguage.value === "zh-Hant" ? "zh-Hant" : adminLanguage.value === "zh-Hans" ? "zh-Hans" : "en"));
const regionAutocompleteOptions = computed(() => regionOptions.value.map((option) => ({ id: option.code, name: `${option.name} · ${option.code}`, code: option.code, searchText: option.searchText })));
const timezoneOptions = computed<TimezoneOption[]>(() => createTimezoneOptions({ locale: adminLanguage.value === "zh-Hant" ? "zh-Hant" : adminLanguage.value === "zh-Hans" ? "zh-Hans" : "en", additionalTimezones: [currentProfile.value.timeZone] }));
const timezoneAutocompleteOptions = computed(() => timezoneOptions.value.map((option) => ({ id: option.value, name: `${option.label} · ${option.value}`, value: option.value, searchText: `${option.label} ${option.value}` })));
const paymentFilterValue = computed<string>({
  get: () => filters.value.paymentMethod ?? "",
  set: (value) => {
    filters.value.paymentMethod = value ? value as PaymentMethod : undefined;
    scheduleDataReload();
  },
});
const navGroups = computed(() => [
  { label: "nav.overview", items: navItems.filter((item) => ["dashboard", "orders", "conflicts"].includes(item.id)) },
  { label: "nav.catalog", items: navItems.filter((item) => item.id === "menu") },
  { label: "nav.store", items: navItems.filter((item) => ["store-profile", "management", "devices"].includes(item.id)) },
  { label: "nav.system", items: navItems.filter((item) => ["account"].includes(item.id)) },
]);
function navItemActive(id: AdminView): boolean {
  if (id === "menu") return view.value === "menu" || view.value === "menu-revisions";
  if (id === "store-profile") return view.value === "store-profile" || view.value === "store-profile-history";
  if (id === "management") return view.value === "management";
  if (id === "devices") return view.value === "devices" || view.value === "device-history";
  if (id === "account") return view.value === "account" || view.value === "account-security";
  return view.value === id;
}
const shellNavGroups = computed<AdminShellNavGroup[]>(() => navGroups.value.map((group) => ({
  label: t(group.label),
  items: group.items.map((item) => ({
    id: item.id,
    label: t(item.label),
    icon: item.icon,
    active: navItemActive(item.id),
  })),
})));
const accountInitials = computed(() => {
  const name = session.value?.displayName?.trim() ?? "";
  const characters = Array.from(name.replace(/\s+/g, ""));
  return characters.slice(0, 2).join("").toUpperCase() || "A";
});
const accountRoleLabel = computed(() => session.value ? t(`ui.${session.value.role}`) : "");
const storeOptions = computed<BaseSelectOption[]>(() => authStores.value.map((store) => ({
  value: store.storeId,
  label: `${store.name} · ${store.storeId}`,
  icon: "Store",
})));
const adminOrderColumns = computed<TableColumn<AdminOrderTableRow>[]>(() => [
  { key: "orderNo", label: t("ui.orderNumber"), sortable: true, width: "15%" },
  { key: "createdAt", label: t("ui.date"), sortable: true, width: "20%" },
  { key: "actorId", label: t("ui.createdBy"), width: "19%" },
  { key: "paymentMethod", label: t("ui.payment"), width: "17%" },
  { key: "totalMinor", label: t("ui.total"), sortable: true, align: "right", width: "14%" },
  { key: "actions", label: t("ui.actions"), align: "right", width: "15%" },
]);
const adminOrderRows = computed<AdminOrderTableRow[]>(() => orders.value.map((order) => ({
  id: order.id,
  orderNo: displayOrderNumber(order),
  createdAt: order.createdAt,
  actorId: order.actorId,
  paymentMethod: order.paymentMethod,
  totalMinor: order.totalMinor,
  status: order.status,
  sourceOrder: order,
})));
const viewTitle = computed(() => t(view.value === "menu-revisions" ? "ui.menuRevisionsPage" : view.value === "store-profile-history" ? "ui.storeActivity" : view.value === "device-history" ? "ui.syncHistory" : view.value === "account-security" ? "ui.securityHistory" : navItems.find((item) => item.id === view.value)?.label ?? "nav.dashboard"));
const viewGroupLabel = computed(() => view.value === "menu-revisions" ? "ui.menuManagement" : view.value === "store-profile-history" ? "ui.storeProfile" : view.value === "device-history" ? "ui.registeredDevices" : view.value === "conflicts" ? "nav.orderHistory" : view.value === "account" || view.value === "account-security" ? "nav.system" : navGroups.value.find((group) => group.items.some((item) => item.id === view.value))?.label ?? "nav.overview");
const breadcrumbItems = computed<BreadcrumbItem[]>(() => {
  if (view.value === "menu-revisions") return [{ label: t("nav.catalog") }, { label: t("ui.menuManagement"), value: "menu" }, { label: viewTitle.value }];
  if (view.value === "account-security") return [{ label: t("nav.system") }, { label: t("ui.account"), value: "account" }, { label: viewTitle.value }];
  if (view.value === "account") return [{ label: t("nav.system") }, { label: viewTitle.value }];
  return [{ label: t(viewGroupLabel.value) }, { label: viewTitle.value }];
});
function handleBreadcrumbClick(item: BreadcrumbItem): void {
  if (typeof item.value === "string") void selectView(item.value as AdminView);
}
const locale = computed(() => adminLanguage.value === "zh-Hant" ? "zh-Hant" : adminLanguage.value === "zh-Hans" ? "zh-Hans" : "en-GB");
const currentProfile = computed(() => {
  if (!profile.value) throw new Error("Authenticated Store profile is unavailable.");
  return profile.value;
});
const storeClockLabel = computed(() => {
  if (!profile.value) return "";
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: currentProfile.value.timeZone,
    weekday: "short",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
  }).formatToParts(storeClock.value).reduce<Record<string, string>>((result, part) => {
    result[part.type] = part.value;
    return result;
  }, {});
  return `${parts.year}-${parts.month}-${parts.day} (${parts.weekday}) ${parts.hour}:${parts.minute}:${parts.second} ${(parts.dayPeriod ?? "").toUpperCase()}`;
});

const canManageUsers = computed(() => !!session.value && hasPermission(session.value.role, "users.manage"));
const canManageDevices = computed(() => !!session.value && hasPermission(session.value.role, "devices.manage"));
const canManageMenu = computed(() => !!session.value && hasPermission(session.value.role, "menu.manage"));
const canResolveConflicts = computed(() => !!session.value && hasPermission(session.value.role, "sync.conflicts.resolve"));
const activeFilterCount = computed(() => [
  filters.value.paymentMethod,
  filters.value.actorId,
  filters.value.minAmountMinor !== undefined,
  filters.value.maxAmountMinor !== undefined,
  filters.value.orderNo,
  filters.value.itemName,
  filters.value.status && filters.value.status !== "all",
].filter(Boolean).length);
const dashboardCards = computed(() => [
  { label: "ui.grossSales", amount: metrics.value.grossSalesMinor, count: metrics.value.activeOrderCount, accent: "text-[var(--pn-text-muted)]" },
  { label: "ui.netSales", amount: metrics.value.netSalesMinor, count: metrics.value.activeOrderCount, accent: "text-[var(--pn-selection-text)]" },
  { label: "ui.cash", amount: metrics.value.cashSalesMinor, count: metrics.value.cashOrderCount, accent: "text-[var(--pn-success)]" },
  { label: "ui.card", amount: metrics.value.cardSalesMinor, count: metrics.value.cardOrderCount, accent: "text-[var(--pn-selection-text)]" },
  { label: "ui.mixed", amount: metrics.value.mixedSalesMinor, count: metrics.value.mixedOrderCount, accent: "text-[var(--pn-warning)]" },
  { label: "ui.refunds", amount: metrics.value.refundSalesMinor, count: metrics.value.refundCount, accent: "text-[var(--pn-danger)]" },
] as const);
const averageOrderMinor = computed(() => metrics.value.activeOrderCount > 0 ? Math.round(metrics.value.netSalesMinor / metrics.value.activeOrderCount) : 0);
const refundRatePercent = computed(() => metrics.value.grossSalesMinor > 0 ? Math.round((metrics.value.refundSalesMinor / metrics.value.grossSalesMinor) * 1000) / 10 : 0);
const paymentMixRows = computed(() => [
  { label: "ui.cash", hint: "ui.cashSalesHint", amount: metrics.value.cashSalesMinor, count: metrics.value.cashOrderCount, tone: "cash", icon: "Banknote" },
  { label: "ui.card", hint: "ui.cardSalesHint", amount: metrics.value.cardSalesMinor, count: metrics.value.cardOrderCount, tone: "card", icon: "CreditCard" },
  { label: "ui.mixed", hint: "ui.mixedSalesHint", amount: metrics.value.mixedSalesMinor, count: metrics.value.mixedOrderCount, tone: "mixed", icon: "WalletCards" },
] as const);
const trendChartPoints = computed<BaseTrendChartPoint[]>(() => trend.value.map((point) => ({ id: point.label, label: point.label })));
const trendChartSeries = computed<BaseTrendChartSeries[]>(() => [
  { id: "orders", label: t('ui.orders'), type: "bar", axisId: "orders", values: trend.value.map((point) => point.orderCount), color: "var(--pn-success)" },
  { id: "sales", label: t('ui.salesAmount'), type: "line", axisId: "sales", values: trend.value.map((point) => point.salesMinor), color: "var(--pn-selection-ring)" },
]);
const trendChartAxes: BaseTrendChartAxis[] = [
  { id: "sales", position: "left", beginAtZero: true },
  { id: "orders", position: "right", beginAtZero: true },
];
const trendValueFormatter = (value: number, series: BaseTrendChartSeries): string => series.id === "sales" ? formatMoney(value) : `${Math.round(value)} ${t('ui.ordersCount')}`;
const trendAxisValueFormatter = (value: number, axisId: string): string => {
  if (axisId !== "sales") return String(Math.round(value));
  if (!profile.value) return "—";
  return new Intl.NumberFormat(locale.value, { style: "currency", currency: profile.value.currency, maximumFractionDigits: 0 }).format(value / 100);
};

function formatTrendLabel(label: string): string {
  if (/^\d{4}-\d{2}-\d{2}$/.test(label)) return label.slice(5).replace("-", "/");
  return label.includes(" ") ? label.slice(label.indexOf(" ") + 1) : label;
}

function formatMoney(minor: number): string {
  if (!profile.value) return "—";
  return new Intl.NumberFormat(locale.value, { style: "currency", currency: profile.value.currency }).format(minor / 100);
}

function metricAmountNeedsHoverLabel(minor: number): boolean {
  return formatMoney(minor).length > 8;
}

function formatDate(value: string): string {
  return new Date(value).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" });
}

function creatorLabel(actorId: string): string {
  const user = managementUsers.value.find((candidate) => candidate.id === actorId);
  return user?.displayName ?? actorId;
}

function paymentTag(method: PaymentMethod): { label: string; icon: string; severity: "success" | "info" | "warning" } {
  if (method === "cash") return { label: "ui.cash", icon: "Banknote", severity: "success" };
  if (method === "card") return { label: "ui.card", icon: "CreditCard", severity: "info" };
  return { label: "ui.mixed", icon: "WalletCards", severity: "warning" };
}

function discountLabel(order: Order): string {
  const labels: string[] = [];
  if ((order.discountByAmountMinor ?? 0) > 0) labels.push(t("ui.amountDiscount"));
  if (order.discountType === "10off") labels.push(t("ui.tenOff"));
  if (order.discountType === "20off") labels.push(t("ui.twentyOff"));
  return labels.join(" + ");
}

function closeOrderDialog(): void {
  orderDialogOpen.value = false;
  orderPaymentDetailsOpen.value = false;
  selectedOrder.value = null;
  selectedOrderEvents.value = [];
}

function handleAdminOrderSort(field: string | keyof AdminOrderTableRow): void {
  if (field === "createdAt" || field === "orderNo" || field === "totalMinor") toggleOrderSort(field);
}

function setPreset(preset: AdminDatePreset): void {
  const range = adminPresetRange(preset, new Date(), currentProfile.value.timeZone, currentProfile.value.businessDayStartMinutes);
  filters.value = { ...filters.value, from: range.from, to: range.to };
  activeDatePreset.value = preset;
  orderOffset.value = 0;
  if (dataReloadTimer !== undefined) {
    window.clearTimeout(dataReloadTimer);
    dataReloadTimer = undefined;
  }
  fromDate.value = range.fromDate;
  toDate.value = range.toDate;
  void loadData();
}

function setDateRange(value: unknown): void {
  if (!Array.isArray(value) || typeof value[0] !== "string" || typeof value[1] !== "string") {
    // Dashboard reports require a valid business-day interval. Clearing the
    // shared picker therefore resets the custom range to the safe default
    // instead of issuing an incomplete request and showing a false auth error.
    setPreset(adminDatePickerClearPreset);
    return;
  }

  const ordered = value[0] <= value[1]
    ? { fromDate: value[0], toDate: value[1] }
    : { fromDate: value[1], toDate: value[0] };
  fromDate.value = ordered.fromDate;
  toDate.value = ordered.toDate;
  filters.value = {
    ...filters.value,
    from: posDateBoundaryIso(ordered.fromDate, currentProfile.value.timeZone),
    to: posDateBoundaryIso(ordered.toDate, currentProfile.value.timeZone, true),
  };
  activeDatePreset.value = null;
  orderOffset.value = 0;
  scheduleDataReload();
}

function scheduleDataReload(): void {
  resetOrderPage();
  dataLoading.value = true;
  if (dataReloadTimer !== undefined) window.clearTimeout(dataReloadTimer);
  dataReloadTimer = window.setTimeout(() => {
    dataReloadTimer = undefined;
    void loadData();
  }, 180);
}

function clearFilters(): void {
  filters.value = { from: filters.value.from, to: filters.value.to, status: "all" };
  scheduleDataReload();
}

function resetOrderPage(): void {
  orderOffset.value = 0;
}

function toggleOrderSort(sortBy: "createdAt" | "orderNo" | "totalMinor"): void {
  if (orderSortBy.value === sortBy) orderSortDirection.value = orderSortDirection.value === "asc" ? "desc" : "asc";
  else {
    orderSortBy.value = sortBy;
    orderSortDirection.value = sortBy === "createdAt" ? "desc" : "asc";
  }
  resetOrderPage();
  void loadData();
}

function moveOrderPage(direction: "next" | "previous"): void {
  if (direction === "next" && orderHasMore.value) orderOffset.value += orderLimit.value;
  if (direction === "previous") orderOffset.value = Math.max(0, orderOffset.value - orderLimit.value);
  void loadData();
}

function handleOrderPagination(change: PaginationChangeEvent): void {
  orderLimit.value = change.pageSize;
  orderOffset.value = (change.page - 1) * change.pageSize;
  void loadData();
}

async function loadData(force = true): Promise<void> {
  if (!session.value) {
    return;
  }
  if (dataReloadTimer !== undefined) {
    window.clearTimeout(dataReloadTimer);
    dataReloadTimer = undefined;
  }
  const query = { ...filters.value, limit: orderLimit.value, offset: orderOffset.value, sortBy: orderSortBy.value, sortDirection: orderSortDirection.value };
  const cacheQuery = JSON.stringify({ query, granularity: granularity.value });
  const cacheKey = resourceKey("orders", cacheQuery) ?? undefined;
  dataLoading.value = true;
  errorMessage.value = "";
  try {
    const remote = await dataResource.load(cacheKey, async (signal) => {
      const requestFetch: typeof fetch = (input, init) => fetch(input, { ...init, signal });
      const result = await fetchRemoteAdminData(query, requestFetch, granularity.value);
      if (!result) throw new Error("Authenticated Admin data is unavailable.");
      return result;
    }, { cache: adminResourceCache, force });
    if (!remote) return;
    orders.value = remote.orders;
    orderTotal.value = remote.total;
    metrics.value = remote.metrics;
    auditRows.value = remote.auditRows;
    trend.value = remote.trend;
    orderHasMore.value = remote.hasMore;
    dataLoaded.value = true;
  } catch (error) {
    if (!(error instanceof DOMException && error.name === "AbortError")) errorMessage.value = error instanceof Error ? error.message : "Unable to load authenticated Admin data";
  } finally {
    dataLoading.value = false;
  }
}

async function openOrder(order: Order, force = false): Promise<void> {
  selectedOrder.value = order;
  orderDialogOpen.value = true;
  orderPaymentDetailsOpen.value = false;
  selectedOrderEvents.value = auditRows.value.filter((event) => event.orderId === order.id).map((event) => ({ id: event.id, label: event.label, occurredAt: event.occurredAt, actorId: event.actorId, reason: event.reason }));
  if (!force || !session.value) return;
  // The row already contains the order snapshot. Keep it visible while an
  // explicit detail reload requests the canonical Server version.
  try {
    const cacheKey = resourceKey("order-detail", order.id) ?? undefined;
    const detail = await orderDetailResource.load(cacheKey, async (signal) => {
      const requestFetch: typeof fetch = (input, init) => fetch(input, { ...init, signal });
      const result = await fetchRemoteAdminOrderDetail(order.id, requestFetch);
      if (!result) throw new Error("Remote order detail is unavailable.");
      return { order: result.order, events: result.events.map((event: OrderEvent) => ({ id: event.id, label: event.type, occurredAt: event.occurredAt, actorId: event.actorId, reason: event.reason })) };
    }, { cache: adminResourceCache, force: true });
    if (!detail) return;
    selectedOrder.value = detail.order;
    selectedOrderEvents.value = detail.events;
  } catch (error) {
    message.value = error instanceof Error ? error.message : "Remote order detail is unavailable; showing the current row.";
  }
}

async function checkSession(): Promise<void> {
  // A fresh Admin login page has no session to restore. Avoid probing the
  // HttpOnly cookie in that state: the expected 401 would be rendered by the
  // browser as a red resource error even though the application is healthy.
  if (readLocalStorage(ADMIN_SESSION_HINT_KEY) !== "1") {
    session.value = null;
    accountProfile.value = null;
    accountSecurityEvents.value = [];
    accountStoreHealth.value = [];
    return;
  }
  try {
    const unified = await authClient.unifiedSession();
    if (unified?.scope === "platform") {
      window.location.replace("/platform/dashboard");
      return;
    }
    session.value = unified?.session ?? null;
    if (!session.value) writeLocalStorage(ADMIN_SESSION_HINT_KEY, "");
  } catch {
    session.value = null;
  }
}

async function revalidateSession(): Promise<void> {
  if (!session.value) return;
  try {
    const next = await authClient.session();
    if (!next) {
      adminResourceCache.clear();
      session.value = null;
      accountProfile.value = null;
      accountSecurityEvents.value = [];
      accountStoreHealth.value = [];
      menuRevisions.value = [];
      managementUsers.value = [];
      managementDevices.value = [];
      identityAuditRows.value = [];
      orders.value = [];
      menu.value = [];
      menuRevisions.value = [];
      managementUsers.value = [];
      managementDevices.value = [];
      dataResource.clear();
      profileResource.clear();
      menuResource.clear();
      managementUsersResource.clear();
      managementDevicesResource.clear();
      deviceHistoryResource.clear();
      identityAuditResource.clear();
      conflictsResource.clear();
      accountResource.clear();
      orderDetailResource.clear();
      message.value = "Admin session expired or was revoked. Sign in again for server data.";
      profile.value = null;
      adminCore.serverSettings.reset();
      return;
    }
    session.value = next;
  } catch {
    // Keep the authenticated server view during a temporary network interruption.
  }
}

async function loadRemoteProfile(force = true): Promise<void> {
  if (!session.value) return;
  const cacheKey = resourceKey("store-profile") ?? undefined;
  const remote = await profileResource.load(cacheKey, async (signal) => {
    const requestFetch: typeof fetch = (input, init) => fetch(input, { ...init, signal });
    const result = await fetchRemoteStoreProfile(requestFetch);
    if (!result) throw new Error("Authenticated Store profile is unavailable.");
    return result;
  }, { cache: adminResourceCache, force });
  if (!remote) throw new Error("Authenticated Store profile is unavailable.");
  profile.value = remote;
  businessDayStartTime.value = minutesToPosTime(remote.businessDayStartMinutes);
  regionSearch.value = remote.regionCode ? `${remote.region} · ${remote.regionCode}` : remote.region;
  timezoneSearch.value = remote.timeZone;
}

async function requestAccountJson(path: string, init: RequestInit = {}, signal?: AbortSignal): Promise<any> {
  const response = await fetch(path, { ...init, signal, credentials: "same-origin", headers: { "content-type": "application/json", ...(init.headers ?? {}) } });
  const body = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(body.error ?? `Account request failed (${response.status})`);
  return body;
}

async function loadAccount(force = true): Promise<void> {
  if (!session.value) return;
  const cacheKey = resourceKey("account", "profile-and-security");
  accountLoading.value = accountProfile.value === null;
  accountRefreshing.value = true;
  try {
    const next = await accountResource.load(cacheKey ?? undefined, async (signal) => {
      const [profileResult, historyResult, healthResult] = await Promise.all([
        requestAccountJson("/api/admin/account", {}, signal),
        requestAccountJson("/api/admin/account/security-history?limit=100", {}, signal),
        requestAccountJson("/api/admin/health/stores", {}, signal),
      ]);
      return { account: profileResult.account as AccountProfile, events: (historyResult.events ?? []) as AccountSecurityEvent[], storeHealth: (healthResult.stores ?? []) as AdminStoreHealth[] };
    }, { cache: adminResourceCache, force });
    if (!next) return;
    accountProfile.value = next.account;
    accountSecurityEvents.value = next.events;
    accountStoreHealth.value = next.storeHealth;
  } catch (error) {
    errorMessage.value = error instanceof Error ? error.message : "Account request failed";
  } finally {
    accountLoading.value = false;
    accountRefreshing.value = false;
  }
}

async function saveAccountProfile(input: { displayName: string; email: string }): Promise<void> {
  if (!session.value) return;
  busy.value = true;
  errorMessage.value = "";
  try {
    await requestAccountJson("/api/admin/account", { method: "PATCH", body: JSON.stringify(input) });
    message.value = t("ui.accountUpdated");
    await loadAccount(true);
  } catch (error) {
    errorMessage.value = error instanceof Error ? error.message : t("ui.accountUpdateFailed");
  } finally { busy.value = false; }
}

async function changeAccountPassword(input: { currentPassword: string; newPassword: string }): Promise<void> {
  if (!session.value) return;
  busy.value = true;
  errorMessage.value = "";
  try {
    await requestAccountJson("/api/admin/account/password", { method: "POST", body: JSON.stringify(input) });
    message.value = t("ui.passwordChangedMessage");
    await loadAccount(true);
  } catch (error) {
    errorMessage.value = error instanceof Error ? error.message : t("ui.passwordChangeFailed");
  } finally { busy.value = false; }
}

async function loadManagement(force = true): Promise<void> {
  if (!session.value) return;
  const devicesView = view.value === "devices" || view.value === "device-history";
  if (devicesView) {
    const cacheKey = resourceKey("devices") ?? undefined;
    const devices = await managementDevicesResource.load(cacheKey, async (signal) => {
      const requestFetch: typeof fetch = (input, init) => fetch(input, { ...init, signal });
      const remote = await fetchRemoteDevices(requestFetch);
      if (!remote) throw new Error("Authenticated devices are unavailable.");
      return remote.devices;
    }, { cache: adminResourceCache, force });
    if (!devices) throw new Error("Authenticated devices are unavailable.");
    managementDevices.value = devices.map(({ health: _health, ...device }) => device);
    managementDeviceHealth.value = devices.flatMap((device) => device.health ? [device.health] : []);
  } else {
    const cacheKey = resourceKey("users") ?? undefined;
    const users = await managementUsersResource.load(cacheKey, async (signal) => {
      const requestFetch: typeof fetch = (input, init) => fetch(input, { ...init, signal });
      const remote = await fetchRemoteUsers(requestFetch);
      if (!remote) throw new Error("Authenticated Store Staff are unavailable.");
      return remote;
    }, { cache: adminResourceCache, force });
    if (!users) throw new Error("Authenticated Store Staff are unavailable.");
    managementUsers.value = users;
  }
  if (view.value === "device-history" && historyEntityId.value && !deviceHistoryOpen.value) {
    const device = managementDevices.value.find((candidate) => candidate.id === historyEntityId.value);
    if (device) void openDeviceHistory(device);
  }
}

async function openDeviceHistory(device: AdminDevice, preserveData = false, requestedPage = preserveData ? deviceHistoryPage.value : 1, requestedPageSize = preserveData ? deviceHistoryPageSize.value : 10): Promise<void> {
  deviceHistoryDevice.value = device;
  deviceHistoryPage.value = requestedPage;
  deviceHistoryPageSize.value = requestedPageSize;
  errorMessage.value = "";
  if (!preserveData) {
    deviceHistoryRows.value = [];
    deviceHistorySecurityVisible.value = false;
    deviceHistoryBootstrapSourceIp.value = undefined;
    deviceHistoryBootstrapUserAgent.value = undefined;
  }
  deviceHistoryOpen.value = true;
  deviceHistoryBusy.value = true;
  try {
    const cacheKey = resourceKey("device-history", `${device.id}:${requestedPage}:${requestedPageSize}`) ?? undefined;
    const result = await deviceHistoryResource.load(cacheKey, async (signal) => {
      const requestFetch: typeof fetch = (input, init) => fetch(input, { ...init, signal });
      const next = await fetchRemoteDeviceSyncHistory(device.id, requestedPage, requestedPageSize, requestFetch);
      if (!next) throw new Error("Authenticated device sync history is unavailable.");
      return next;
    }, { cache: adminResourceCache, force: preserveData });
    // An older request may be aborted after a newer request takes ownership.
    // The resource controller returns undefined for that stale observation;
    // it is not an API failure and must not surface a false error banner.
    if (!result) {
      if (deviceHistoryResource.error == null) return;
      throw new Error("Authenticated device sync history is unavailable.");
    }
    deviceHistoryRows.value = result.events;
    deviceHistorySecurityVisible.value = result.securityMetadataVisible;
    deviceHistoryBootstrapSourceIp.value = result.bootstrapSourceIp;
    deviceHistoryBootstrapUserAgent.value = result.bootstrapUserAgent;
    deviceHistoryPage.value = result.page;
    deviceHistoryPageSize.value = result.pageSize;
    deviceHistoryTotal.value = result.total;
  } catch (error) {
    if (error instanceof DOMException && error.name === "AbortError") return;
    errorMessage.value = error instanceof Error ? error.message : "Device sync history is unavailable.";
  } finally {
    deviceHistoryBusy.value = false;
  }
}

function changeDeviceHistoryPage(change: PaginationChangeEvent): void {
  if (!deviceHistoryDevice.value) return;
  void openDeviceHistory(deviceHistoryDevice.value, true, change.page, change.pageSize);
}

async function loadMenu(force = true): Promise<void> {
  if (!session.value) {
    menu.value = [];
    menuCategories.value = [];
    menuRevisions.value = [];
    menuSyncStatus.value = null;
    menuSavedToken.value = "";
    menuSyncStatus.value = null;
    return;
  }
  const cacheKey = resourceKey("menu") ?? undefined;
  const snapshot = await menuResource.load(cacheKey, async (signal) => {
    const requestFetch: typeof fetch = (input, init) => fetch(input, { ...init, signal });
    const [revisions, syncStatusResult] = await Promise.all([fetchMenuRevisions(requestFetch), fetchMenuSyncStatus(requestFetch)]);
    if (!revisions) throw new Error("Authenticated server menu revisions are unavailable.");
    if (!revisions.some((revision) => revision.status === "published")) return { revisions, syncStatus: syncStatusResult, published: null };
    const published = await fetchPublishedMenu(requestFetch);
    if (!published) throw new Error("Published server menu is unavailable.");
    return { revisions, syncStatus: syncStatusResult, published: { items: published.items, categories: published.categories } };
  }, { cache: adminResourceCache, force });
  if (!snapshot) throw new Error("Authenticated server menu is unavailable.");
  menuRevisions.value = snapshot.revisions;
  menuSyncStatus.value = snapshot.syncStatus;
  menu.value = snapshot.published?.items ?? [];
  menuCategories.value = snapshot.published?.categories ?? [];
}

async function loadIdentityAudit(force = true): Promise<void> {
  if (!session.value) { identityAuditRows.value = []; return; }
  const cacheKey = resourceKey("identity-audit") ?? undefined;
  identityAuditRows.value = await identityAuditResource.load(cacheKey, async (signal) => {
    const requestFetch: typeof fetch = (input, init) => fetch(input, { ...init, signal });
    return await fetchRemoteIdentityAudit(requestFetch) ?? [];
  }, { cache: adminResourceCache, force }) ?? [];
}

async function loadConflicts(force = true): Promise<void> {
  if (!canResolveConflicts.value) return;
  try {
    const cacheKey = resourceKey("conflicts") ?? undefined;
    syncConflicts.value = await conflictsResource.load(cacheKey, async (signal) => {
      const requestFetch: typeof fetch = (input, init) => fetch(input, { ...init, signal });
      return await fetchRemoteSyncConflicts(requestFetch) ?? [];
    }, { cache: adminResourceCache, force }) ?? [];
  } catch (error) {
    errorMessage.value = error instanceof Error ? error.message : "Unable to load server sync conflicts";
  }
}

async function resolveConflict(conflict: ServerSyncConflict, action: ConflictResolutionAction): Promise<void> {
  if (!canResolveConflicts.value) return;
  const labels: Record<ConflictResolutionAction, string> = {
    "keep-server": "Keep server version",
    "accept-local-as-correction": "Accept local as correction event",
    "reject-local": "Reject local version",
  };
  const reason = window.prompt(`${labels[action]} reason`, "Reviewed sync conflict")?.trim();
  if (!reason) return;
  busy.value = true;
  try {
    const result = await resolveRemoteSyncConflict(conflict.id, action, reason, fetch);
    if (!result) throw new Error("Conflict resolution was not authorised or available");
    message.value = `Conflict ${conflict.id} resolved: ${labels[action]}.`;
    await loadConflicts();
  } catch (error) {
    errorMessage.value = error instanceof Error ? error.message : "Unable to resolve sync conflict";
  } finally { busy.value = false; }
}

async function loginRemote(): Promise<void> {
  authBusy.value = true;
  authError.value = "";
  bootstrapError.value = "";
  profile.value = null;
  shellLoading.value = true;
  try {
    if (authStage.value === "credentials") {
      const options = await withTimeout(authClient.unifiedOptions({ method: "admin-password", email: authForm.value.email.trim(), secret: authForm.value.secret }), "Authentication timed out. Try again.");
      if (options.scope === "platform") {
        await withTimeout(authClient.unifiedLogin({ method: "admin-password", email: authForm.value.email.trim(), secret: authForm.value.secret }), "Platform login timed out. Try again.");
        authForm.value.secret = "";
        window.location.assign("/platform/dashboard");
        return;
      }
      if (!options.stores.length) throw new Error("No Store is available for this account");
      authStores.value = options.stores;
      if (authStores.value.length > 1) {
        authStoreId.value = "";
        authStage.value = "store";
        shellLoading.value = false;
        loading.value = false;
        return;
      }
      authStoreId.value = authStores.value[0]!.storeId;
    }
    if (!authStoreId.value) throw new Error("Select a Store");
    loading.value = true;
    const unified = await withTimeout(authClient.unifiedLogin({ method: "admin-password", email: authForm.value.email.trim(), secret: authForm.value.secret, storeId: authStoreId.value }), "Admin login timed out. Try again.");
    if (unified.scope !== "store") throw new Error("Unexpected platform scope for Store Admin login");
    session.value = unified.session;
    authStage.value = "credentials";
    canonicalizeAdminPath();
    writeLocalStorage(ADMIN_SESSION_HINT_KEY, "1");
    writeLocalStorage("posnext.admin-email", authForm.value.email.trim().toLowerCase());
    writeLocalStorage("posnext.store-id", session.value.storeId);
    authForm.value.secret = "";
    await withTimeout((async () => {
      await loadRemoteProfile();
      await loadMenu();
      await loadManagement();
      await loadIdentityAudit();
      await loadConflicts();
      if (view.value === "account" || view.value === "account-security") await loadAccount(false);
    })(), "Admin Store bootstrap timed out. Try again.");
    dataLoaded.value = false;
    setPreset("today");
    shellLoading.value = false;
    loading.value = false;
    if (sessionCheckTimer === undefined) sessionCheckTimer = window.setInterval(() => { void revalidateSession(); }, 60_000);
  } catch (error) {
    session.value = null;
      accountProfile.value = null;
      accountSecurityEvents.value = [];
      accountStoreHealth.value = [];
    writeLocalStorage(ADMIN_SESSION_HINT_KEY, "");
    profile.value = null;
    adminCore.serverSettings.reset();
    authError.value = error instanceof Error ? error.message : "Admin login failed";
    shellLoading.value = false;
    loading.value = false;
  } finally { authBusy.value = false; }
}

function openStoreSwitcher(): void {
  if (!session.value || !authStores.value.length) return;
  accountPopoverOpen.value = false;
  pendingStoreId.value = session.value.storeId;
  storeSwitchDialogOpen.value = true;
}

async function switchAdminStore(): Promise<void> {
  if (!session.value || !authStoreId.value || authStoreId.value === session.value.storeId) {
    storeSwitchDialogOpen.value = false;
    return;
  }
  const previousStoreId = session.value.storeId;
  storeSwitchBusy.value = true;
  loading.value = true;
  authError.value = "";
  try {
    const nextSession = await authClient.switchStore({ storeId: authStoreId.value });
    adminResourceCache.clear();
    session.value = nextSession;
    writeLocalStorage("posnext.store-id", session.value.storeId);
    await loadRemoteProfile();
    await loadMenu();
    await loadManagement();
    await loadIdentityAudit();
    await loadConflicts();
    await loadAccount(true);
    dataLoaded.value = false;
    setPreset("today");
    message.value = `Switched to ${currentProfile.value.name}.`;
    storeSwitchDialogOpen.value = false;
  } catch (error) {
    authStoreId.value = previousStoreId;
    authError.value = error instanceof Error ? error.message : "Unable to switch Store";
  } finally { storeSwitchBusy.value = false; loading.value = false; }
}

async function confirmStoreSwitch(): Promise<void> {
  if (!pendingStoreId.value || pendingStoreId.value === session.value?.storeId) {
    storeSwitchDialogOpen.value = false;
    return;
  }
  authStoreId.value = pendingStoreId.value;
  await switchAdminStore();
}

async function switchStoreFromAccount(storeId: string): Promise<void> {
  if (!session.value || storeId === session.value.storeId) return;
  pendingStoreId.value = storeId;
  await confirmStoreSwitch();
}

async function logoutRemote(): Promise<void> {
  authBusy.value = true;
  try {
    await authClient.unifiedLogout();
    adminResourceCache.clear();
    writeLocalStorage(ADMIN_SESSION_HINT_KEY, "");
    session.value = null;
    accountProfile.value = null;
    accountSecurityEvents.value = [];
    accountStoreHealth.value = [];
    orders.value = [];
    menu.value = [];
    menuCategories.value = [];
    menuRevisions.value = [];
    managementUsers.value = [];
    managementDevices.value = [];
    identityAuditRows.value = [];
    syncConflicts.value = [];
    profile.value = null;
    dataResource.clear();
    profileResource.clear();
    menuResource.clear();
    managementUsersResource.clear();
    managementDevicesResource.clear();
    deviceHistoryResource.clear();
    identityAuditResource.clear();
    conflictsResource.clear();
    accountResource.clear();
    orderDetailResource.clear();
    dataLoading.value = false;
    dataLoaded.value = false;
    adminCore.serverSettings.reset();
    authStores.value = [];
    authForm.value.secret = "";
  } catch (error) { authError.value = error instanceof Error ? error.message : "Admin logout failed"; } finally { loading.value = false; authBusy.value = false; }
}

async function confirmLogout(): Promise<void> {
  const confirmed = await confirm({
    title: t("ui.confirmSignOutTitle"),
    message: t("ui.confirmSignOutMessage"),
    severity: "warning",
    icon: "LogOut",
    confirmLabel: t("ui.confirmSignOut"),
    cancelLabel: t("ui.cancel"),
  });
  if (confirmed) await logoutRemote();
}

function openOperatorStore(): void {
  accountPopoverOpen.value = false;
  window.open("https://pos-staging.moonq.io/", "_blank", "noopener,noreferrer");
}

async function refreshMenuRevisions(): Promise<void> {
  if (!canManageMenu.value) {
    menuRevisions.value = [];
    menuSyncStatus.value = null;
    return;
  }
  await loadMenu(true);
}

async function saveMenuDraft(input: MenuRevisionInput): Promise<void> {
  if (!canManageMenu.value) return;
  menuBusy.value = true;
  try {
    const draftItems = input.items.map((item) => ({
      ...item,
      updatedAt: new Date().toISOString(),
      optionGroups: item.optionGroups?.map((group) => ({
        ...group,
        options: group.options.map((option) => ({ ...option })),
      })),
    }));
    const draftInput = { categories: input.categories, items: draftItems };
    menu.value = draftItems;
    menuCategories.value = input.categories ?? [];
    const revision = await createMenuDraft(draftInput, fetch);
    if (!revision) throw new Error("Server menu draft was not authorised or available");
    const published = await publishMenuRevision(revision.id, fetch);
    if (!published) throw new Error("Menu publish was not authorised or available");
    menu.value = published.items;
    menuCategories.value = published.categories;
    menuSavedToken.value = new Date().toISOString();
    message.value = `${t("ui.menuSavedAndPublished")} ${published.revision}.`;
    await refreshMenuRevisions();
  } catch (error) {
    errorMessage.value = error instanceof Error ? error.message : t("ui.menuDraftSaveFailed");
  } finally { menuBusy.value = false; }
}

async function publishMenu(revisionId: string): Promise<void> {
  if (!canManageMenu.value) return;
  menuBusy.value = true;
  try {
    const published = await publishMenuRevision(revisionId, fetch);
    if (!published) throw new Error("Menu publish was not authorised or available");
    menu.value = published.items;
    menuCategories.value = published.categories;
    message.value = `${t("ui.menuRevisionPublished")} ${published.revision}.`;
    await refreshMenuRevisions();
  } catch (error) {
    errorMessage.value = error instanceof Error ? error.message : t("ui.menuRevisionPublishFailed");
  } finally { menuBusy.value = false; }
}

async function rollbackMenu(revisionId: string): Promise<void> {
  if (!canManageMenu.value) return;
  menuBusy.value = true;
  try {
    const published = await rollbackMenuRevision(revisionId, fetch);
    if (!published) throw new Error("Menu rollback was not authorised or available");
    menu.value = published.items;
    menuCategories.value = published.categories;
    message.value = `${t("ui.menuRevisionRestored")} ${published.revision}.`;
    await refreshMenuRevisions();
  } catch (error) {
    errorMessage.value = error instanceof Error ? error.message : t("ui.menuRevisionRollbackFailed");
  } finally { menuBusy.value = false; }
}


async function createManagedUser(input: AdminUserCreate): Promise<void> {
  if (!session.value || !canManageUsers.value) return;
  busy.value = true;
  try {
    const userId = await createRemoteUser(input, fetch);
    if (!userId) throw new Error("User creation was not authorised or available");
    await loadManagement();
    message.value = t("ui.userCreated");
  } catch (error) {
    errorMessage.value = error instanceof Error ? error.message : t("ui.userCreateFailed");
  } finally { busy.value = false; }
}

async function updateManagedUser(userId: string, update: AdminUserUpdate): Promise<void> {
  if (!session.value) return;
  busy.value = true;
  try {
    if (!await updateRemoteUser(userId, update, fetch)) throw new Error("User update was not authorised or available");
    await loadManagement();
    message.value = t("ui.userUpdated");
  } catch (error) {
    errorMessage.value = error instanceof Error ? error.message : t("ui.userUpdateFailed");
  } finally { busy.value = false; }
}

async function removeManagedUser(user: AdminUser): Promise<void> {
  const confirmed = await confirm({ title: t("ui.removeUserTitle"), message: t("ui.removeUserMessage"), severity: "danger", icon: "Trash2", confirmLabel: t("ui.remove"), cancelLabel: t("ui.cancel") });
  if (!confirmed || !session.value) return;
  busy.value = true;
  try {
    if (!await deleteRemoteUser(user.id, fetch)) throw new Error("User removal was not authorised or available");
    await loadManagement();
    message.value = t("ui.userRemoved");
  } catch (error) {
    errorMessage.value = error instanceof Error ? error.message : t("ui.userRemoveFailed");
  } finally { busy.value = false; }
}


async function saveDevice(device: AdminDevice): Promise<void> {
  if (!session.value) return;
  busy.value = true;
  try {
    if (!await updateRemoteDevice(device.id, { active: device.active }, fetch)) throw new Error("Remote device update was not authorised or available");
    await loadManagement();
    message.value = `Device ${device.label} updated on the authenticated store.`;
  } catch (error) {
    errorMessage.value = error instanceof Error ? error.message : "Unable to update device";
    await loadManagement();
  } finally { busy.value = false; }
}

async function editDevice(device: AdminDevice): Promise<void> {
  if (!session.value || !canManageDevices.value) return;
  const label = window.prompt("Device label:", device.label);
  if (label === null) return;
  const clientId = window.prompt("Client ID:", device.clientId);
  if (clientId === null) return;
  const deviceType = window.prompt("Device type (operator or bridge):", device.deviceType);
  if (deviceType === null) return;
  if (!["operator", "bridge"].includes(deviceType)) {
    errorMessage.value = "Device type must be operator or bridge";
    return;
  }
  busy.value = true;
  try {
    if (!await updateRemoteDevice(device.id, { label, clientId, deviceType: deviceType as AdminDevice["deviceType"] }, fetch)) throw new Error("Remote device update was not authorised or available");
    await loadManagement();
    message.value = `Device ${device.id} updated.`;
  } catch (error) {
    errorMessage.value = error instanceof Error ? error.message : "Unable to edit device";
  } finally { busy.value = false; }
}

async function updateManagedDevice(deviceId: string, update: AdminDeviceUpdate): Promise<void> {
  if (!session.value || !canManageDevices.value) return;
  busy.value = true;
  try {
    if (!await updateRemoteDevice(deviceId, update, fetch)) throw new Error("Device update was not authorised or available");
    await loadManagement();
    message.value = t("ui.deviceUpdated");
  } catch (error) {
    errorMessage.value = error instanceof Error ? error.message : t("ui.deviceUpdateFailed");
  } finally { busy.value = false; }
}

async function toggleManagedDevice(device: AdminDevice): Promise<void> {
  if (!session.value || !canManageDevices.value) return;
  busy.value = true;
  try {
    if (!await updateRemoteDevice(device.id, { active: !device.active }, fetch)) throw new Error("Device state update was not authorised or available");
    await loadManagement();
    message.value = device.active ? t("ui.deviceDisabled") : t("ui.deviceEnabled");
  } catch (error) {
    errorMessage.value = error instanceof Error ? error.message : t("ui.deviceUpdateFailed");
  } finally { busy.value = false; }
}


async function deactivateDevice(device: AdminDevice): Promise<void> {
  if (!session.value || !window.confirm(`Deactivate ${device.label}? Existing audit and sync identity will be retained.`)) return;
  busy.value = true;
  try {
    if (!await deleteRemoteDevice(device.id, fetch)) throw new Error("Device deactivation was not authorised or available");
    await loadManagement();
    message.value = `Device ${device.label} deactivated.`;
  } catch (error) {
    errorMessage.value = error instanceof Error ? error.message : "Unable to deactivate device";
  } finally { busy.value = false; }
}

async function saveProfile(): Promise<void> {
  if (!session.value) return;
  busy.value = true;
  try {
    currentProfile.value.businessDayStartMinutes = posTimeToMinutes(businessDayStartTime.value);
    const remote = await updateRemoteStoreProfile(currentProfile.value, fetch);
    if (!remote) throw new Error("Authenticated Store profile update was not available.");
    profile.value = remote;
    message.value = t("ui.storeProfileSaved");
  } catch (error) {
    errorMessage.value = error instanceof Error ? error.message : "Unable to save Store Profile";
  } finally { busy.value = false; }
}

function selectRegion(option: { code: string; name: string }): void {
  currentProfile.value.regionCode = option.code;
  currentProfile.value.region = option.name.split(" · ")[0];
  regionSearch.value = option.name;
}

function selectTimezone(option: { value: string; name: string }): void {
  currentProfile.value.timeZone = option.value;
  timezoneSearch.value = option.name;
}

function downloadCsv(): void {
  void fetchRemoteOrdersCsv({ ...filters.value, limit: 100 }, fetch).then((csv) => {
    if (!csv) {
      message.value = "Remote export is unavailable; no server export was generated.";
      return;
    }
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
    const link = document.createElement("a");
    link.href = url;
    link.download = `posnext-orders-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    message.value = "CSV export generated from the authenticated server.";
  });
}

function downloadExcel(): void {
  void fetchRemoteOrdersExcel({ ...filters.value, limit: 10_000, sortBy: orderSortBy.value, sortDirection: orderSortDirection.value }, fetch).then((body) => {
    if (!body) {
      message.value = "Remote Excel export is unavailable; no server export was generated.";
      return;
    }
    const content = body;
    const url = URL.createObjectURL(new Blob([content], { type: "application/vnd.ms-excel" }));
    const link = document.createElement("a");
    link.href = url;
    link.download = `posnext-orders-${new Date().toISOString().slice(0, 10)}.xls`;
    link.click();
    URL.revokeObjectURL(url);
    message.value = "Excel export generated from the authenticated server.";
  });
}

async function correctOrder(targetOrder: Order | null = selectedOrder.value): Promise<void> {
  if (!targetOrder || !session.value) {
    errorMessage.value = "An authenticated Admin session is required for corrections.";
    return;
  }
  if (!hasPermission(session.value.role, "orders.void")) {
    errorMessage.value = "Your role cannot perform this correction.";
    return;
  }
  const confirmed = await confirm({
    title: t("ui.confirmVoidTitle"),
    message: t("ui.confirmVoidMessage"),
    details: `${t("ui.orderNumber")} #${displayOrderNumber(targetOrder)}`,
    severity: "danger",
    icon: "Trash2",
    confirmLabel: t("ui.confirmVoid"),
    cancelLabel: t("ui.cancel"),
  });
  if (!confirmed) return;
  const reason = window.prompt("Void reason", "Admin correction")?.trim();
  if (!reason) return;
  try {
    const input: AdminCorrectionRequest = { type: "void", reason };
    if (!await applyRemoteOrderCorrection(targetOrder.id, input, fetch)) throw new Error("Remote correction was not authorised or available");
    if (selectedOrder.value?.id === targetOrder.id) closeOrderDialog();
    message.value = "Void recorded on the authenticated store.";
    await loadData();
  } catch (error) {
    errorMessage.value = error instanceof Error ? error.message : "Unable to record correction";
  }
}

async function voidOrderFromTable(row: AdminOrderTableRow): Promise<void> {
  if (row.status !== "completed") return;
  await correctOrder(row.sourceOrder);
}

async function voidOrderFromDetail(): Promise<void> {
  if (selectedOrder.value?.status === "completed") await correctOrder(selectedOrder.value);
}

function cashChangeMinor(order: Order): number | null {
  const received = order.payment?.cashReceivedMinor;
  if (received === undefined) return null;
  return Math.max(0, received - (order.payment?.cashMinor ?? 0));
}

function isMenuView(target: AdminView): boolean {
  return target === "menu" || target === "menu-revisions";
}

type ReloadReason = "manual" | "entry" | "visibility" | "online" | "mutation" | "store-switch" | "bootstrap";

async function reloadViewData(target: AdminView, reason: ReloadReason): Promise<void> {
  switch (target) {
    case "dashboard":
    case "orders":
      await loadData(reason !== "entry");
      if (target === "orders" && selectedOrder.value) await openOrder(selectedOrder.value, true);
      return;
    case "menu":
    case "menu-revisions":
      await loadMenu(reason !== "entry");
      return;
    case "store-profile":
      await loadRemoteProfile(reason !== "entry");
      return;
    case "store-profile-history":
      await loadIdentityAudit(reason !== "entry");
      return;
    case "management":
    case "devices":
      await loadManagement(reason !== "entry");
      return;
    case "device-history":
      await loadManagement(reason !== "entry");
      // loadManagement opens the history once for an entry route. A second
      // request here used to abort the first request and create a false race
      // error on the initial page open. Only explicit reloads re-fetch it.
      if (reason !== "entry" && deviceHistoryDevice.value) await openDeviceHistory(deviceHistoryDevice.value, true);
      return;
    case "conflicts":
      await loadConflicts(reason !== "entry");
      return;
    case "account":
    case "account-security":
      await loadAccount(reason !== "entry");
      return;
  }
}

async function reloadCurrentView(reason: ReloadReason = "manual"): Promise<void> {
  if (!session.value || pageReloadBusy.value || storeSwitchBusy.value) return;
  pageReloadBusy.value = true;
  try {
    await reloadViewData(view.value, reason);
  } catch (error) {
    errorMessage.value = error instanceof Error ? error.message : "Unable to reload the current Admin page";
  } finally {
    pageReloadBusy.value = false;
  }
}

async function selectView(next: AdminView, updateHistory = true, entityId: string | null = null): Promise<void> {
  const leavingDirtyMenu = menuDraftDirty.value && view.value === "menu" && next !== "menu";
  if (leavingDirtyMenu) {
    const confirmed = await confirm({
      title: t("ui.leaveMenuWithChangesTitle"),
      message: t("ui.leaveMenuWithChangesMessage"),
      severity: "warning",
      icon: "AlertTriangle",
      confirmLabel: t("ui.leaveWithoutSaving"),
      cancelLabel: t("ui.cancel"),
    });
    if (!confirmed) {
      if (!updateHistory && typeof window !== "undefined") window.history.pushState({ view: view.value }, "", adminPathFor(view.value, historyEntityId.value));
      return;
    }
    menuDraftDirty.value = false;
  }
  if (updateHistory && typeof window !== "undefined") {
    const nextPath = adminPathFor(next, entityId);
    if (window.location.pathname !== nextPath) window.history.pushState({ view: next }, "", nextPath);
  }
  view.value = next;
  historyEntityId.value = entityId;
  message.value = "";
  errorMessage.value = "";
  void reloadCurrentView("entry");
}

function handleAdminPopState(): void { const route = routeFromPath(window.location.pathname); void selectView(route.view, false, route.entityId ?? null); }

function handleAdminBeforeUnload(event: BeforeUnloadEvent): void {
  if (!menuDraftDirty.value) return;
  event.preventDefault();
  event.returnValue = "";
}

function refreshOnline(): void {
  online.value = typeof navigator === "undefined" ? true : navigator.onLine;
  if (online.value) void reloadCurrentView();
}

function refreshOnVisibility(): void {
  if (document.visibilityState === "visible") void reloadCurrentView();
}

function updateDateCalendarCount(): void {
  dateCalendarCount.value = dateCalendarMedia?.matches ? "single" : "dual";
}

async function applyPwaUpdate(): Promise<void> {
  pwaUpdateBusy.value = true;
  try {
    const result = await requestPwaUpdate();
    if (result !== "updated") pwaUpdateVisible.value = false;
  } finally {
    pwaUpdateBusy.value = false;
  }
}

function showPwaUpdate(): void {
  pwaUpdateVisible.value = true;
}

function reloadPage(): void {
  window.location.reload();
}

function canonicalizeAdminPath(): void {
  const expectedPath = adminPathFor(view.value, historyEntityId.value);
  if (window.location.pathname !== expectedPath) window.history.replaceState({ view: view.value }, "", expectedPath);
}

onMounted(async () => {
  dateCalendarMedia = window.matchMedia("(max-width: 760px)");
  updateDateCalendarCount();
  dateCalendarMedia.addEventListener("change", updateDateCalendarCount);
  if (platformMode) {
    shellLoading.value = false;
    loading.value = false;
    return;
  }
  applyTheme(adminTheme.value);
  storeClockTimer = window.setInterval(() => { storeClock.value = new Date(); }, 1_000);
  try {
    await withTimeout(checkSession(), "Admin session check timed out. Reload and try again.");
  } catch (error) {
    session.value = null;
    bootstrapError.value = error instanceof Error ? error.message : "Admin session check timed out. Reload and try again.";
    loading.value = false;
  }
  if (session.value && !bootstrapError.value) canonicalizeAdminPath();
  if (session.value && !bootstrapError.value) {
    try {
      await withTimeout((async () => {
        authStoreId.value = session.value!.storeId;
        try { authStores.value = await authClient.stores(); } catch { authStores.value = []; }
        await applyUiLanguage();
        await loadRemoteProfile();
        await loadMenu();
        await loadManagement();
        await loadIdentityAudit();
        await loadConflicts();
        if (view.value === "account" || view.value === "account-security") await loadAccount(false);
        dataLoaded.value = false;
        setPreset("today");
      })(), "Admin Store bootstrap timed out. Reload and try again.");
      loading.value = false;
      sessionCheckTimer = window.setInterval(() => { void revalidateSession(); }, 60_000);
    } catch (error) {
      bootstrapError.value = error instanceof Error ? error.message : "Authenticated Admin bootstrap failed";
      loading.value = false;
    }
  } else if (!bootstrapError.value) {
    await applyUiLanguage();
    loading.value = false;
  }
  // Keep the Vue shell overlay over the app until every value required by the
  // authenticated shell (especially Store profile) is ready to render.
  shellLoading.value = false;
  window.addEventListener("online", refreshOnline);
  window.addEventListener("offline", refreshOnline);
  document.addEventListener("visibilitychange", refreshOnVisibility);
  window.addEventListener("popstate", handleAdminPopState);
  window.addEventListener("beforeunload", handleAdminBeforeUnload);
  window.addEventListener(PWA_UPDATE_EVENT, showPwaUpdate);
  systemThemeMedia = window.matchMedia("(prefers-color-scheme: dark)");
  systemThemeMedia.addEventListener("change", handleSystemThemeChange);
});

watch(adminLanguage, () => { void applyUiLanguage(); });
onUpdated(() => { translateDocument(adminLanguage.value); });

onBeforeUnmount(() => {
  if (storeClockTimer !== undefined) window.clearInterval(storeClockTimer);
  if (sessionCheckTimer !== undefined) window.clearInterval(sessionCheckTimer);
  if (dataReloadTimer !== undefined) window.clearTimeout(dataReloadTimer);
  dataResource.clear();
  profileResource.clear();
  menuResource.clear();
  managementUsersResource.clear();
  managementDevicesResource.clear();
  deviceHistoryResource.clear();
  identityAuditResource.clear();
  conflictsResource.clear();
  accountResource.clear();
  orderDetailResource.clear();
  systemThemeMedia?.removeEventListener("change", handleSystemThemeChange);
  dateCalendarMedia?.removeEventListener("change", updateDateCalendarCount);
  window.removeEventListener("online", refreshOnline);
  window.removeEventListener("offline", refreshOnline);
  document.removeEventListener("visibilitychange", refreshOnVisibility);
  window.removeEventListener("popstate", handleAdminPopState);
  window.removeEventListener("beforeunload", handleAdminBeforeUnload);
  window.removeEventListener(PWA_UPDATE_EVENT, showPwaUpdate);
});
</script>

<template>
  <PlatformConsole v-if="platformMode" :audit-only="platformAuditOnly" :users-only="platformUsersOnly" />
  <div v-else class="min-h-screen bg-[var(--pn-bg)] text-[var(--pn-text)]">
    <AppLoadingOverlay v-if="shellLoading || (session && !profile)" app-name="POSNext Admin" icon-src="/admin-icon.svg?v=20260814" />
    <main v-else-if="bootstrapError" class="grid min-h-screen place-items-center px-4 py-10">
      <section class="w-full max-w-lg rounded-2xl border border-[var(--pn-border)] bg-[var(--pn-surface)] p-6 text-[var(--pn-text)] shadow-xl sm:p-8">
        <div class="flex items-center gap-3">
          <img src="/admin-icon.svg?v=20260814" alt="" class="size-12 rounded-2xl" />
          <div><p class="text-xs text-[var(--pn-text-muted)]">Bootstrap</p><h1 class="text-2xl font-semibold tracking-tight">Store data unavailable</h1></div>
        </div>
        <p class="mt-5 text-sm leading-6 text-[var(--pn-text-muted)]">The authenticated Store profile could not be loaded. No Admin data view was opened.</p>
        <BaseAlert class="mt-4" severity="warning" :dismissible="false">{{ bootstrapError }}</BaseAlert>
        <div class="mt-6 flex gap-3">
          <BaseButton class="flex-1" variant="secondary" @click="reloadPage">Reload</BaseButton>
          <BaseButton class="flex-1" variant="primary" :disabled="authBusy" @click="void logoutRemote()">Sign out</BaseButton>
        </div>
      </section>
    </main>
    <main v-else-if="!session" class="grid min-h-screen place-items-center px-4 py-10">
      <section class="w-full max-w-lg rounded-2xl border border-[var(--pn-border)] bg-[var(--pn-surface)] p-6 text-[var(--pn-text)] shadow-xl sm:p-8">
        <div class="flex items-center gap-3">
          <img src="/admin-icon.svg?v=20260814" alt="" class="size-12 rounded-2xl" />
          <div><h1 class="text-3xl font-semibold tracking-tight">Sign in</h1></div>
        </div>
        <p class="mt-5 text-sm leading-6 text-[var(--pn-text-muted)]">{{ authStage === 'credentials' ? 'Sign in to continue.' : 'Select a Store to continue.' }}</p>
        <form class="mt-6 space-y-4" @submit.prevent="loginRemote">
          <template v-if="authStage === 'credentials'">
            <label class="block text-sm text-[var(--pn-text-muted)]">Email<BaseInput v-model="authForm.email" class="mt-1" autocomplete="email" required type="email" /></label>
            <label class="block text-sm text-[var(--pn-text-muted)]">Password<BaseInput v-model="authForm.secret" class="mt-1" autocomplete="current-password" required type="password" /></label>
          </template>
          <template v-else>
            <label class="block text-sm text-[var(--pn-text-muted)]">Store<BaseSelect v-model="authStoreId" class="mt-1 w-full" :options="storeOptions" required aria-label="Store" /></label>
          </template>
          <BaseButton class="w-full" type="submit" variant="primary" size="lg" :disabled="authBusy || (authStage === 'credentials' ? !authForm.email || !authForm.secret : !authStoreId)">{{ authBusy ? 'Signing in…' : authStage === 'credentials' ? 'Sign in' : 'Continue' }}</BaseButton>
          <BaseButton v-if="authStage === 'store'" class="w-full" type="button" variant="secondary" :disabled="authBusy" @click="authStage = 'credentials'; authStores = []; authStoreId = ''; authForm.secret = ''">Back</BaseButton>
        </form>
        <BaseAlert v-if="authError" class="mt-4" severity="warning" @close="authError = ''">{{ authError }}</BaseAlert>
      </section>
    </main>
    <template v-else>
    <AdminShell
      :nav-groups="shellNavGroups"
      :refresh-label="t('ui.refresh')"
      :refreshing="pageReloadBusy"
      :refresh-disabled="storeSwitchBusy"
      :drawer-theme="filterDrawerTheme"
      @navigate="(id) => void selectView(id as AdminView)"
      @refresh="void reloadCurrentView()"
    >
      <template #brand>
        <div class="flex min-w-0 items-center gap-[.6rem]"><img src="/admin-icon.svg?v=20260814" alt="" class="size-8 rounded-[.65rem] shadow-[0_6px_14px_rgb(8_127_152_/_20%)]" /><div class="text-[.95rem] font-[750] tracking-[-.01em]">POSNext Admin</div></div>
      </template>
      <template #header-context>
        <div v-if="session" class="flex min-w-0 items-center gap-2 max-[760px]:hidden"><span class="grid size-7 shrink-0 place-items-center rounded-[.55rem] bg-[color-mix(in_srgb,var(--pn-brand)_12%,var(--pn-surface))] text-[var(--pn-brand)]" aria-hidden="true"><BaseIcon name="Store" :size="16" decorative /></span><div class="grid min-w-0 gap-px justify-items-end text-right"><span class="max-w-96 truncate text-xs font-[750] text-[var(--pn-text)]">{{ currentProfile.name }}</span><small class="truncate text-[.625rem] text-[var(--pn-text-muted)]">{{ storeClockLabel }}</small></div></div>
      </template>
      <template #header-actions>
        <BasePopoverButton v-if="session" v-model:visible="accountPopoverOpen" class="min-h-[2.35rem] gap-[.45rem] px-[.55rem] py-[.3rem] pl-[.35rem] max-[760px]:w-10 max-[760px]:justify-center max-[760px]:px-1" variant="secondary" size="sm" placement="bottom-end" has-popup="menu" popover-class-name="rounded-[.9rem] shadow-[0_18px_45px_rgb(15_23_42_/_28%)]" popover-padding="p-2" :min-width-px="240" :aria-label="`${session.displayName} account menu`">
          <span class="inline-grid size-[1.7rem] shrink-0 place-items-center rounded-full bg-[var(--pn-brand)] text-[var(--pn-brand-contrast)] text-[.625rem] font-extrabold tracking-[.02em] leading-none" aria-hidden="true">{{ accountInitials }}</span><span class="grid min-w-0 gap-px justify-items-start text-left max-[760px]:hidden"><span class="max-w-32 truncate text-[.6875rem] font-[750] text-[var(--pn-text)]">{{ session.displayName }}</span><small class="text-[.5625rem] leading-tight text-[var(--pn-text-muted)]">{{ accountRoleLabel }}</small></span>
          <template #popover><div class="grid gap-2"><div class="flex items-center gap-[.65rem] px-[.2rem] pb-[.35rem] pt-[.2rem]"><span class="inline-grid size-9 shrink-0 place-items-center rounded-full bg-[var(--pn-brand)] text-[var(--pn-brand-contrast)] text-xs font-extrabold" aria-hidden="true">{{ accountInitials }}</span><div class="grid min-w-0 gap-[.15rem]"><strong class="truncate text-xs text-[var(--pn-text)]">{{ session.displayName }}</strong><span class="text-[.6875rem] text-[var(--pn-text-muted)]">{{ accountRoleLabel }}</span></div></div><BaseButton class="w-full !justify-start gap-[.55rem] text-[var(--pn-text)]" variant="ghost" size="sm" :disabled="storeSwitchBusy || !authStores.length" @click="openStoreSwitcher"><BaseIcon name="Store" :size="15" decorative /><span>{{ t('ui.switchStore') }}</span></BaseButton><BaseButton class="w-full !justify-start gap-[.55rem] text-[var(--pn-text)]" variant="ghost" size="sm" @click="openOperatorStore"><BaseIcon name="ExternalLink" :size="15" decorative /><span>{{ t('ui.openStore') }}</span></BaseButton><BaseButton class="w-full !justify-start gap-[.55rem] text-[var(--pn-text)]" variant="ghost" size="sm" @click="accountPopoverOpen = false; void selectView('account')"><BaseIcon name="UserCircle" :size="15" decorative /><span>{{ t('ui.account') }}</span></BaseButton><div class="h-px bg-[var(--pn-border)]" role="separator"></div><BaseButton class="w-full !justify-start text-[var(--pn-text-muted)]" variant="ghost" size="sm" :disabled="authBusy" @click="accountPopoverOpen = false; void confirmLogout()"><BaseIcon name="LogOut" :size="15" decorative /><span>{{ t('nav.signOut') }}</span></BaseButton></div></template>
        </BasePopoverButton>
      </template>
      <template #breadcrumb><BaseBreadcrumb :items="breadcrumbItems" size="sm" density="compact" @click="handleBreadcrumbClick" /></template>
        <BaseAlert v-if="message" class="mb-4" severity="success" :dismissible="true" @close="message = ''">{{ message }}</BaseAlert>
        <BaseAlert v-if="errorMessage" class="mb-4" severity="error" @close="errorMessage = ''">{{ errorMessage }}</BaseAlert>
        <BaseAlert v-if="authError" class="mb-4" severity="warning" @close="authError = ''">{{ authError }}</BaseAlert>
        <KeepAlive :max="1"><AccountView v-if="view === 'account' || view === 'account-security'" :key="session?.storeId ?? 'signed-out'" :language="adminLanguage" :theme="adminTheme" :time-zone="profile?.timeZone ?? 'UTC'" :security-only="view === 'account-security'" :store-switching="storeSwitchBusy" :account="accountProfile" :events="accountSecurityEvents" :store-health="accountStoreHealth" :loading="accountLoading" :refreshing="accountRefreshing" :busy="busy" @security-history="selectView('account-security')" @back-account="selectView('account')" @switch-store="switchStoreFromAccount" @save-profile="saveAccountProfile" @change-password="changeAccountPassword" @language-change="adminCore.language.setLanguage" @theme-change="applyTheme" /></KeepAlive>
        <template v-if="view === 'dashboard' || view === 'orders'">
          <div class="mb-6 grid gap-4 rounded-2xl border border-[var(--pn-border)] bg-[var(--pn-surface)] p-4 text-[var(--pn-text)] shadow-xl">
            <div class="flex flex-wrap items-center gap-[.65rem]">
              <div class="flex flex-wrap gap-[.4rem]"><BaseButton v-for="preset in dashboardPresets" :key="preset.id" class="min-h-8 rounded-lg !px-[.65rem] !py-[.35rem] !text-[.6875rem]" size="sm" :variant="activeDatePreset === preset.id ? 'primary' : 'secondary'" :loading="dataLoading && activeDatePreset === preset.id" :loading-label="t('ui.loading')" @click="setPreset(preset.id)">{{ t(preset.label) }}</BaseButton></div>
              <div class="ml-auto flex min-w-0 items-center gap-[.45rem] max-[760px]:w-full max-[760px]:flex-col max-[760px]:items-stretch">
                <BaseDatePicker
                  id="admin-date-range"
                  :model-value="adminDateRangeValue"
                  mode="range"
                  :calendar-count="dateCalendarCount"
                  :adapter="posDateRangeAdapter"
                  :locale="adminDatePickerLocale"
                  :time-zone="currentProfile.timeZone"
                  :aria-label="t('ui.dateRange')"
                  :loading="dataLoading"
                  :loading-label="t('ui.loading')"
                  variant="outlined"
                  @update:model-value="setDateRange"
                />
              </div>
              <BaseButton class="min-h-[2.35rem]" variant="secondary" size="sm" :aria-label="t('ui.moreFilters')" @click="filterDrawerOpen = true">
                <span>{{ t('ui.moreFilters') }}</span><span v-if="activeFilterCount" class="rounded-full bg-[var(--pn-selection-bg)] px-1.5 text-xs">{{ activeFilterCount }}</span>
              </BaseButton>
            </div>
          </div>

          <BaseDrawer v-model:visible="filterDrawerOpen" position="right" :theme="filterDrawerTheme" :title="t('ui.moreFilters')" :subtitle="activeFilterCount ? `${activeFilterCount} ${t('ui.activeFilters')}` : undefined" width="min(26rem, 92vw)" :aria-label="t('ui.moreFilters')">
            <div class="grid gap-4 pt-1">
              <div v-if="activeFilterCount" class="flex justify-end"><BaseButton size="sm" variant="ghost" @click="clearFilters">{{ t('ui.clear') }}</BaseButton></div>
              <label class="block text-xs text-[var(--pn-text-muted)]">{{ t('ui.payment') }}<PosSelect v-model="paymentFilterValue" :options="paymentOptions" class="mt-1 w-full" /></label>
              <label class="block text-xs text-[var(--pn-text-muted)]">{{ t('ui.status') }}<PosSelect v-model="filters.status" :options="statusOptions" class="mt-1 w-full" @update:model-value="scheduleDataReload" /></label>
              <div class="grid grid-cols-2 gap-3"><label class="block text-xs text-[var(--pn-text-muted)]">{{ t('ui.minAmount') }}<BaseInput :model-value="filters.minAmountMinor ?? ''" type="number" min="0" class="mt-1 w-full" @update:model-value="filters.minAmountMinor = Number($event) || undefined; scheduleDataReload()" /></label><label class="block text-xs text-[var(--pn-text-muted)]">{{ t('ui.maxAmount') }}<BaseInput :model-value="filters.maxAmountMinor ?? ''" type="number" min="0" class="mt-1 w-full" @update:model-value="filters.maxAmountMinor = Number($event) || undefined; scheduleDataReload()" /></label></div>
              <label class="block text-xs text-[var(--pn-text-muted)]">{{ t('ui.operatorLabel') }}<BaseInput v-model="filters.actorId" class="mt-1 w-full" @update:model-value="scheduleDataReload" /></label>
              <label class="block text-xs text-[var(--pn-text-muted)]">{{ t('ui.orderNumber') }}<BaseInput v-model="filters.orderNo" class="mt-1 w-full" @update:model-value="scheduleDataReload" /></label>
              <label class="block text-xs text-[var(--pn-text-muted)]">Item name<BaseInput v-model="filters.itemName" class="mt-1 w-full" @update:model-value="scheduleDataReload" /></label>
            </div>
          </BaseDrawer>

          <div v-if="view === 'dashboard'" class="mb-3 grid gap-3 md:grid-cols-[minmax(0,1.12fr)_minmax(22rem,.88fr)]">
            <section class="relative min-w-0 overflow-hidden rounded-2xl border border-[var(--pn-border)] bg-[var(--pn-surface)] bg-[radial-gradient(circle_at_100%_0%,color-mix(in_srgb,var(--pn-brand)_22%,transparent),transparent_45%)] p-[1.35rem] text-[var(--pn-text)] shadow-xl">
              <div class="flex items-start justify-between gap-4">
              <div><span class="inline-flex items-center gap-[.3rem] text-[.6875rem] font-extrabold uppercase tracking-[.13em] text-[var(--pn-text-subtle)]">{{ t('ui.netSales') }}<BaseTooltip :theme="filterDrawerTheme" :text="t('ui.netSalesHint')" /></span><div class="mt-[.45rem] text-[clamp(2.35rem,5vw,3.5rem)] font-extrabold tracking-[-.065em] leading-[.95] text-[var(--pn-selection-text)]"><span v-if="dataLoading && !dataLoaded" class="block overflow-hidden rounded-[.45rem] bg-[linear-gradient(90deg,color-mix(in_srgb,var(--pn-text-muted)_14%,transparent)_25%,color-mix(in_srgb,var(--pn-text-muted)_28%,transparent)_50%,color-mix(in_srgb,var(--pn-text-muted)_14%,transparent)_75%)] bg-[length:200%_100%] animate-[skeleton-shimmer_1.4s_ease-in-out_infinite] w-44 h-14" aria-hidden="true" /><Transition v-else mode="out-in" v-bind="metricTransition"><span :key="metrics.netSalesMinor">{{ formatMoney(metrics.netSalesMinor) }}</span></Transition></div></div>
                <span class="inline-flex items-center gap-[.35rem] whitespace-nowrap rounded-full border border-[color-mix(in_srgb,var(--pn-brand)_35%,var(--pn-border))] bg-[color-mix(in_srgb,var(--pn-brand)_9%,var(--pn-surface))] px-[.6rem] py-[.35rem] text-[.6875rem] font-bold text-[var(--pn-brand)]"><template v-if="dataLoading && !dataLoaded"><span class="block overflow-hidden rounded-[.45rem] bg-[linear-gradient(90deg,color-mix(in_srgb,var(--pn-text-muted)_14%,transparent)_25%,color-mix(in_srgb,var(--pn-text-muted)_28%,transparent)_50%,color-mix(in_srgb,var(--pn-text-muted)_14%,transparent)_75%)] bg-[length:200%_100%] animate-[skeleton-shimmer_1.4s_ease-in-out_infinite] w-20 h-4" aria-hidden="true" /></template><Transition v-else mode="out-in" v-bind="metricTransition"><span :key="metrics.activeOrderCount"><BaseIcon name="CheckCircle2" :size="14" decorative />{{ metrics.activeOrderCount }} {{ t('ui.ordersCount') }}</span></Transition></span>
              </div>
              <div class="mt-[2.4rem] grid grid-cols-2 gap-4 border-t border-[var(--pn-border)] pt-[.9rem]"><div class="grid gap-1"><span class="text-[.6875rem] font-semibold text-[var(--pn-text-muted)]">{{ t('ui.ordersCount') }}</span><strong><span v-if="dataLoading && !dataLoaded" class="block overflow-hidden rounded-[.45rem] bg-[linear-gradient(90deg,color-mix(in_srgb,var(--pn-text-muted)_14%,transparent)_25%,color-mix(in_srgb,var(--pn-text-muted)_28%,transparent)_50%,color-mix(in_srgb,var(--pn-text-muted)_14%,transparent)_75%)] bg-[length:200%_100%] animate-[skeleton-shimmer_1.4s_ease-in-out_infinite] w-[4.5rem] h-[1.1rem]" aria-hidden="true" /><Transition v-else mode="out-in" v-bind="metricTransition"><span :key="metrics.activeOrderCount">{{ metrics.activeOrderCount }}</span></Transition></strong></div><div class="grid gap-1"><span class="text-[.6875rem] font-semibold text-[var(--pn-text-muted)]">{{ t('ui.averageOrder') }}</span><strong><span v-if="dataLoading && !dataLoaded" class="block overflow-hidden rounded-[.45rem] bg-[linear-gradient(90deg,color-mix(in_srgb,var(--pn-text-muted)_14%,transparent)_25%,color-mix(in_srgb,var(--pn-text-muted)_28%,transparent)_50%,color-mix(in_srgb,var(--pn-text-muted)_14%,transparent)_75%)] bg-[length:200%_100%] animate-[skeleton-shimmer_1.4s_ease-in-out_infinite] w-[4.5rem] h-[1.1rem]" aria-hidden="true" /><Transition v-else mode="out-in" v-bind="metricTransition"><span :key="averageOrderMinor">{{ formatMoney(averageOrderMinor) }}</span></Transition></strong></div></div>
            </section>
            <section class="min-w-0 rounded-2xl border border-[var(--pn-border)] bg-[var(--pn-surface)] p-[1.35rem] text-[var(--pn-text)] shadow-xl">
              <div class="mb-4 flex items-start justify-between gap-4"><h2 class="inline-flex items-center gap-[.3rem] text-base font-bold">{{ t('ui.paymentMix') }}<BaseTooltip :theme="filterDrawerTheme" :text="t('ui.paymentMixHint')" /></h2><BaseIcon name="Database" :size="18" class="text-[var(--pn-text-subtle)]" decorative /></div>
              <div class="grid gap-4"><div v-for="payment in paymentMixRows" :key="payment.label" class="grid gap-[.45rem]"><div class="grid grid-cols-[minmax(0,1fr)_auto] items-baseline gap-3 text-xs text-[var(--pn-text-muted)]"><span class="inline-flex items-center gap-[.3rem]"><BaseIcon :name="payment.icon" :size="14" class="shrink-0 text-[var(--pn-text-muted)]" decorative />{{ t(payment.label) }}<BaseTooltip :theme="filterDrawerTheme" :text="t(payment.hint)" /></span><strong class="inline-flex items-baseline justify-end gap-[.65rem] whitespace-nowrap text-right text-[.8125rem] font-bold text-[var(--pn-text)]"><template v-if="dataLoading && !dataLoaded"><span class="block overflow-hidden rounded-[.45rem] bg-[linear-gradient(90deg,color-mix(in_srgb,var(--pn-text-muted)_14%,transparent)_25%,color-mix(in_srgb,var(--pn-text-muted)_28%,transparent)_50%,color-mix(in_srgb,var(--pn-text-muted)_14%,transparent)_75%)] bg-[length:200%_100%] animate-[skeleton-shimmer_1.4s_ease-in-out_infinite] w-[4.5rem] h-[1.1rem]" aria-hidden="true" /><small class="block overflow-hidden rounded-[.45rem] bg-[linear-gradient(90deg,color-mix(in_srgb,var(--pn-text-muted)_14%,transparent)_25%,color-mix(in_srgb,var(--pn-text-muted)_28%,transparent)_50%,color-mix(in_srgb,var(--pn-text-muted)_14%,transparent)_75%)] bg-[length:200%_100%] animate-[skeleton-shimmer_1.4s_ease-in-out_infinite] w-[4.5rem] h-[.85rem]" aria-hidden="true" /></template><Transition v-else mode="out-in" v-bind="metricTransition"><span :key="`${payment.amount}-${payment.count}`" class="inline-flex items-baseline justify-end gap-[.65rem]"><span>{{ formatMoney(payment.amount) }}</span><small class="text-[.6875rem] font-medium text-[var(--pn-text-muted)]">{{ payment.count }} {{ t('ui.ordersCount') }}</small></span></Transition></strong></div><div class="h-[.45rem] overflow-hidden rounded-full bg-[var(--pn-surface-muted)]"><div v-if="dataLoading && !dataLoaded" class="block overflow-hidden rounded-[.45rem] bg-[linear-gradient(90deg,color-mix(in_srgb,var(--pn-text-muted)_14%,transparent)_25%,color-mix(in_srgb,var(--pn-text-muted)_28%,transparent)_50%,color-mix(in_srgb,var(--pn-text-muted)_14%,transparent)_75%)] bg-[length:200%_100%] animate-[skeleton-shimmer_1.4s_ease-in-out_infinite] w-full h-full h-full" aria-hidden="true" /><div v-else class="h-full min-w-1 rounded-full transition-[width] duration-300" :class="payment.tone === 'cash' ? 'bg-[var(--pn-success)]' : payment.tone === 'card' ? 'bg-[var(--pn-selection-ring)]' : 'bg-[var(--pn-warning)]'" :style="{ width: `${Math.min(100, Math.round((payment.amount / Math.max(metrics.netSalesMinor, 1)) * 100))}%` }" /></div></div></div>
            </section>
          </div>

          <div v-if="view === 'dashboard'" class="mb-6 grid grid-cols-3 gap-3 max-[760px]:flex max-[760px]:snap-x max-[760px]:snap-mandatory max-[760px]:overflow-x-auto max-[760px]:pb-2">
            <div class="grid min-w-0 gap-[.35rem] rounded-2xl border border-[var(--pn-border)] bg-[var(--pn-surface)] p-4 text-[var(--pn-text)] shadow-xl max-[760px]:min-w-[10.5rem] max-[760px]:shrink-0 max-[760px]:snap-start"><span class="inline-flex items-center gap-[.3rem] text-[.6875rem] text-[var(--pn-text-muted)]">{{ t('ui.grossSales') }}<BaseTooltip :theme="filterDrawerTheme" :text="t('ui.grossSalesHint')" /></span><strong class="text-[clamp(1.3rem,2.2vw,1.75rem)] font-extrabold tracking-[-.045em]"><span v-if="dataLoading && !dataLoaded" class="block overflow-hidden rounded-[.45rem] bg-[linear-gradient(90deg,color-mix(in_srgb,var(--pn-text-muted)_14%,transparent)_25%,color-mix(in_srgb,var(--pn-text-muted)_28%,transparent)_50%,color-mix(in_srgb,var(--pn-text-muted)_14%,transparent)_75%)] bg-[length:200%_100%] animate-[skeleton-shimmer_1.4s_ease-in-out_infinite] w-[4.5rem] h-[1.1rem]" aria-hidden="true" /><Transition v-else mode="out-in" v-bind="metricTransition"><span :key="metrics.grossSalesMinor">{{ formatMoney(metrics.grossSalesMinor) }}</span></Transition></strong><small class="text-[.6875rem] text-[var(--pn-text-subtle)]">{{ t('ui.grossSalesHint') }}</small></div>
            <div class="grid min-w-0 gap-[.35rem] rounded-2xl border border-[var(--pn-border)] bg-[var(--pn-surface)] p-4 text-[var(--pn-text)] shadow-xl max-[760px]:min-w-[10.5rem] max-[760px]:shrink-0 max-[760px]:snap-start"><span class="inline-flex items-center gap-[.3rem] text-[.6875rem] text-[var(--pn-text-muted)]">{{ t('ui.refunds') }}<BaseTooltip :theme="filterDrawerTheme" :text="t('ui.refundSummaryHint')" /></span><strong class="text-[clamp(1.3rem,2.2vw,1.75rem)] font-extrabold tracking-[-.045em]" :class="metrics.refundSalesMinor > 0 || metrics.refundCount > 0 ? 'text-[var(--pn-danger)]' : 'text-[var(--pn-text)]'"><span v-if="dataLoading && !dataLoaded" class="block overflow-hidden rounded-[.45rem] bg-[linear-gradient(90deg,color-mix(in_srgb,var(--pn-text-muted)_14%,transparent)_25%,color-mix(in_srgb,var(--pn-text-muted)_28%,transparent)_50%,color-mix(in_srgb,var(--pn-text-muted)_14%,transparent)_75%)] bg-[length:200%_100%] animate-[skeleton-shimmer_1.4s_ease-in-out_infinite] w-[4.5rem] h-[1.1rem]" aria-hidden="true" /><Transition v-else mode="out-in" v-bind="metricTransition"><span :key="metrics.refundSalesMinor">{{ formatMoney(metrics.refundSalesMinor) }}</span></Transition></strong><small class="text-[.6875rem] text-[var(--pn-text-subtle)]"><template v-if="dataLoading && !dataLoaded"><span class="block overflow-hidden rounded-[.45rem] bg-[linear-gradient(90deg,color-mix(in_srgb,var(--pn-text-muted)_14%,transparent)_25%,color-mix(in_srgb,var(--pn-text-muted)_28%,transparent)_50%,color-mix(in_srgb,var(--pn-text-muted)_14%,transparent)_75%)] bg-[length:200%_100%] animate-[skeleton-shimmer_1.4s_ease-in-out_infinite] w-24 h-3" aria-hidden="true" /></template><Transition v-else mode="out-in" v-bind="metricTransition"><span :key="metrics.refundCount">{{ metrics.refundCount }} {{ t('ui.ordersCount') }}</span></Transition></small></div>
            <div class="grid min-w-0 gap-[.35rem] rounded-2xl border border-[var(--pn-border)] bg-[var(--pn-surface)] p-4 text-[var(--pn-text)] shadow-xl max-[760px]:min-w-[10.5rem] max-[760px]:shrink-0 max-[760px]:snap-start"><span class="inline-flex items-center gap-[.3rem] text-[.6875rem] text-[var(--pn-text-muted)]">{{ t('ui.refundRate') }}<BaseTooltip :theme="filterDrawerTheme" :text="t('ui.refundRateHint')" /></span><strong class="text-[clamp(1.3rem,2.2vw,1.75rem)] font-extrabold tracking-[-.045em]"><span v-if="dataLoading && !dataLoaded" class="block overflow-hidden rounded-[.45rem] bg-[linear-gradient(90deg,color-mix(in_srgb,var(--pn-text-muted)_14%,transparent)_25%,color-mix(in_srgb,var(--pn-text-muted)_28%,transparent)_50%,color-mix(in_srgb,var(--pn-text-muted)_14%,transparent)_75%)] bg-[length:200%_100%] animate-[skeleton-shimmer_1.4s_ease-in-out_infinite] w-[4.5rem] h-[1.1rem]" aria-hidden="true" /><Transition v-else mode="out-in" v-bind="metricTransition"><span :key="refundRatePercent">{{ refundRatePercent }}%</span></Transition></strong><small class="text-[.6875rem] text-[var(--pn-text-subtle)]">{{ t('ui.refundRateHint') }}</small></div>
          </div>

          <div v-else class="mb-6 grid grid-cols-6 gap-3 max-[1100px]:grid-cols-3 max-[760px]:flex max-[760px]:snap-x max-[760px]:snap-mandatory max-[760px]:overflow-x-auto max-[760px]:pb-2"><div v-for="card in dashboardCards" :key="card.label" class="grid min-w-0 gap-[.35rem] rounded-2xl border border-[var(--pn-border)] bg-[var(--pn-surface)] p-4 text-[var(--pn-text)] shadow-xl max-[760px]:min-w-[10.5rem] max-[760px]:shrink-0 max-[760px]:snap-start"><div class="flex items-center justify-between gap-2"><span class="text-[.6875rem] font-semibold text-[var(--pn-text-muted)]">{{ t(card.label) }}</span><span class="size-[.45rem] rounded-full shadow-[0_0_0_4px_color-mix(in_srgb,currentColor_13%,transparent)]" :class="card.accent" /></div><div v-basehoverlabel="metricAmountNeedsHoverLabel(card.amount) ? { text: formatMoney(card.amount), placement: 'top' } : undefined" class="mt-[.35rem] truncate text-[clamp(1.25rem,2vw,1.65rem)] font-extrabold tracking-[-.035em]" :class="card.accent"><span v-if="dataLoading && !dataLoaded" class="block h-[1.65rem] w-20 animate-[skeleton-shimmer_1.4s_ease-in-out_infinite] overflow-hidden rounded-[.45rem] bg-[linear-gradient(90deg,color-mix(in_srgb,var(--pn-text-muted)_14%,transparent)_25%,color-mix(in_srgb,var(--pn-text-muted)_28%,transparent)_50%,color-mix(in_srgb,var(--pn-text-muted)_14%,transparent)_75%)] bg-[length:200%_100%]" aria-hidden="true" /><Transition v-else mode="out-in" v-bind="metricTransition"><span :key="`${card.label}-${card.amount}`">{{ formatMoney(card.amount) }}</span></Transition></div><div class="text-[.6875rem] text-[var(--pn-text-muted)]"><span v-if="dataLoading && !dataLoaded" class="block h-3 w-14 animate-[skeleton-shimmer_1.4s_ease-in-out_infinite] overflow-hidden rounded bg-[linear-gradient(90deg,color-mix(in_srgb,var(--pn-text-muted)_14%,transparent)_25%,color-mix(in_srgb,var(--pn-text-muted)_28%,transparent)_50%,color-mix(in_srgb,var(--pn-text-muted)_14%,transparent)_75%)] bg-[length:200%_100%]" aria-hidden="true" /><Transition v-else mode="out-in" v-bind="metricTransition"><span :key="`${card.label}-${card.count}`"><span class="font-bold text-[var(--pn-text)]">{{ card.count }}</span> {{ t('ui.ordersCount') }}</span></Transition></div></div></div>

          <div v-if="view === 'dashboard'" class="rounded-2xl border border-[var(--pn-border)] bg-[var(--pn-surface)] p-5 text-[var(--pn-text)] shadow-xl"><div class="mb-5 flex items-start justify-between gap-4"><div><h2 class="inline-flex items-center gap-[.3rem] text-base font-bold">{{ t('ui.salesTrend') }}<BaseTooltip :theme="filterDrawerTheme" :text="t('ui.salesTrendHint')" /></h2></div><div class="flex items-center gap-3 max-[760px]:items-end max-[760px]:flex-col"><div class="inline-flex items-center gap-[.85rem] text-[.6875rem] text-[var(--pn-text-muted)]"><span class="inline-flex items-center gap-[.35rem] whitespace-nowrap"><i class="inline-block h-[.16rem] w-[1.2rem] rounded-full bg-[var(--pn-selection-ring)]" />{{ t('ui.salesAmount') }}</span><span class="inline-flex items-center gap-[.35rem] whitespace-nowrap"><i class="inline-block h-[.7rem] w-[.85rem] rounded-[.15rem] border border-[var(--pn-success)] bg-[color-mix(in_srgb,var(--pn-success)_65%,transparent)]" />{{ t('ui.orders') }}</span></div><PosSelect v-model="granularity" :options="granularityOptions" class="w-32" :aria-label="t('ui.granularity')" @change="scheduleDataReload" /></div></div><div v-if="dataLoading && !dataLoaded" class="grid gap-3 pt-4" aria-label="Loading sales trend"><span class="block overflow-hidden rounded-[.45rem] bg-[linear-gradient(90deg,color-mix(in_srgb,var(--pn-text-muted)_14%,transparent)_25%,color-mix(in_srgb,var(--pn-text-muted)_28%,transparent)_50%,color-mix(in_srgb,var(--pn-text-muted)_14%,transparent)_75%)] bg-[length:200%_100%] animate-[skeleton-shimmer_1.4s_ease-in-out_infinite] w-full h-[1.15rem]" /><span class="block overflow-hidden rounded-[.45rem] bg-[linear-gradient(90deg,color-mix(in_srgb,var(--pn-text-muted)_14%,transparent)_25%,color-mix(in_srgb,var(--pn-text-muted)_28%,transparent)_50%,color-mix(in_srgb,var(--pn-text-muted)_14%,transparent)_75%)] bg-[length:200%_100%] animate-[skeleton-shimmer_1.4s_ease-in-out_infinite] w-[78%] h-[1.15rem]" /></div><BaseTrendChart v-else-if="trend.length" :points="trendChartPoints" :series="trendChartSeries" :axes="trendChartAxes" :theme="filterDrawerTheme" :label-formatter="formatTrendLabel" :value-formatter="trendValueFormatter" :axis-value-formatter="trendAxisValueFormatter" :aria-label="t('ui.salesTrendHint')" height="19rem" /><div v-else class="p-10 text-center text-sm text-[var(--pn-text-muted)]">{{ t('ui.noSalesForRange') }}</div></div>

          <div v-if="view === 'orders'" class="rounded-2xl border border-[var(--pn-border)] bg-[var(--pn-surface)] p-5 shadow-xl max-[760px]:rounded-none max-[760px]:border-0 max-[760px]:bg-transparent max-[760px]:p-0 max-[760px]:shadow-none">
            <div class="mb-4 flex flex-wrap items-center justify-between gap-3"><div><h2 class="font-semibold">Orders</h2><p class="mt-1 text-xs text-[var(--pn-text-subtle)]">{{ orderTotal }} {{ t('ui.ordersCount') }}</p></div><div class="flex flex-wrap gap-2"><BaseButton size="sm" variant="secondary" @click="downloadCsv">Export CSV</BaseButton><BaseButton size="sm" variant="secondary" @click="downloadExcel">Export Excel</BaseButton></div></div>
            <BaseTable
              :data="adminOrderRows"
              :columns="adminOrderColumns"
              row-key="id"
              :loading="dataLoading"
              :loading-label="t('ui.loadingOrderDetail')"
              :empty-message="t('ui.noMatchingOrders')"
              :sort-field="orderSortBy"
              :sort-direction="orderSortDirection"
              class="min-w-0 max-[760px]:hidden"
              @sort="handleAdminOrderSort"
              @row-click="(row) => openOrder(row.sourceOrder)"
            >
              <template #cell-orderNo="{ value, row }"><div class="font-semibold">#{{ value }}<BaseTag v-if="row.status === 'voided'" :label="t('ui.voided')" severity="secondary" size="sm" class="mt-1" /></div></template>
              <template #cell-createdAt="{ value }"><span class="text-sm">{{ formatDate(String(value)) }}</span></template>
              <template #cell-actorId="{ value }"><span>{{ creatorLabel(String(value)) }}</span></template>
              <template #cell-paymentMethod="{ value }"><BaseTag :label="t(paymentTag(value as PaymentMethod).label)" :icon="paymentTag(value as PaymentMethod).icon" :severity="paymentTag(value as PaymentMethod).severity" rounded size="sm" /></template>
              <template #cell-totalMinor="{ value }"><span class="font-semibold">{{ formatMoney(Number(value)) }}</span></template>
              <template #cell-actions="{ row }"><div class="flex justify-end gap-1" @click.stop><BaseButton size="sm" variant="secondary" :aria-label="t('ui.detail')" :title="t('ui.detail')" @click="openOrder(row.sourceOrder)">{{ t('ui.detail') }}</BaseButton><BaseButton v-if="row.status === 'completed'" size="sm" variant="ghost" icon="Trash2" :aria-label="t('ui.void')" :title="t('ui.void')" @click="voidOrderFromTable(row)" /></div></template>
            </BaseTable>
            <div class="mt-3 hidden max-[760px]:grid gap-2" :aria-busy="dataLoading || undefined">
              <template v-if="dataLoading && !dataLoaded">
                <div v-for="index in 5" :key="`mobile-order-skeleton-${index}`" class="grid grid-cols-[minmax(0,1fr)_5rem] gap-3 rounded-xl border border-[var(--pn-border)] bg-[var(--pn-bg)] p-3" aria-hidden="true">
                  <span class="h-4 w-24 animate-[skeleton-shimmer_1.4s_ease-in-out_infinite] rounded bg-[var(--pn-surface-muted)]" /><span class="h-4 w-16 animate-[skeleton-shimmer_1.4s_ease-in-out_infinite] rounded bg-[var(--pn-surface-muted)]" /><span class="h-3 w-36 animate-[skeleton-shimmer_1.4s_ease-in-out_infinite] rounded bg-[var(--pn-surface-muted)]" /><span class="h-3 w-12 animate-[skeleton-shimmer_1.4s_ease-in-out_infinite] rounded bg-[var(--pn-surface-muted)]" />
                </div>
              </template>
              <template v-else>
                <article v-for="row in adminOrderRows" :key="`mobile-${row.id}`" class="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-3 rounded-xl border border-[var(--pn-border)] bg-[var(--pn-bg)] p-3 transition-colors hover:bg-[var(--pn-surface-muted)]" @click="openOrder(row.sourceOrder)">
                  <div class="min-w-0">
                    <div class="flex min-w-0 items-center gap-2"><strong class="truncate text-sm">#{{ row.orderNo }}</strong><BaseTag :label="t(paymentTag(row.paymentMethod).label)" :icon="paymentTag(row.paymentMethod).icon" :severity="paymentTag(row.paymentMethod).severity" rounded size="sm" /></div>
                    <div class="mt-1 flex min-w-0 flex-wrap gap-x-3 gap-y-1 text-xs text-[var(--pn-text-muted)]"><span class="truncate">{{ formatDate(row.createdAt) }}</span><span class="truncate">{{ creatorLabel(row.actorId) }}</span></div>
                  </div>
                  <div class="grid justify-items-end gap-1"><strong class="whitespace-nowrap text-sm">{{ formatMoney(row.totalMinor) }}</strong><div class="flex items-center gap-1" @click.stop><BaseHoverLabel :text="t('ui.detail')" placement="top"><template #trigger><BaseButton size="sm" variant="ghost" icon="Eye" :aria-label="t('ui.detail')" @click="openOrder(row.sourceOrder)" /></template></BaseHoverLabel><BaseHoverLabel v-if="row.status === 'completed'" :text="t('ui.void')" placement="top"><template #trigger><BaseButton size="sm" variant="ghost" icon="Trash2" :aria-label="t('ui.void')" @click="voidOrderFromTable(row)" /></template></BaseHoverLabel></div></div>
                </article>
              </template>
              <p v-if="!dataLoading && !adminOrderRows.length" class="rounded-xl border border-dashed border-[var(--pn-border)] p-6 text-center text-sm text-[var(--pn-text-muted)]">{{ t('ui.noMatchingOrders') }}</p>
            </div>
            <BasePagination v-model="orderPage" v-model:page-size="orderLimit" class="mt-4 border-t border-[var(--pn-border)] pt-4 max-[760px]:hidden" :total="orderTotal" :page-sizes="[10, 20, 50]" :loading="dataLoading" :total-label="t('ui.total')" :records-label="t('ui.ordersCount')" :rows-label="t('ui.rows')" :page-label="t('ui.page')" :pagination-label="t('ui.pagination')" :first-page-label="t('ui.firstPage')" :previous-page-label="t('ui.previousPage')" :next-page-label="t('ui.nextPage')" :last-page-label="t('ui.lastPage')" @change="handleOrderPagination" />
            <BasePagination v-model="orderPage" v-model:page-size="orderLimit" compact class="mt-4 hidden w-full justify-center border-t border-[var(--pn-border)] pt-4 max-[760px]:flex" :total="orderTotal" :page-sizes="[10, 20, 50]" :loading="dataLoading" :total-label="t('ui.total')" :records-label="t('ui.ordersCount')" :rows-label="t('ui.rows')" :page-label="t('ui.page')" :pagination-label="t('ui.pagination')" :first-page-label="t('ui.firstPage')" :previous-page-label="t('ui.previousPage')" :next-page-label="t('ui.nextPage')" :last-page-label="t('ui.lastPage')" @change="handleOrderPagination" />
          </div>
        </template>

        <div v-else-if="view === 'menu' || view === 'menu-revisions'" class="space-y-5">
          <BaseAlert v-if="!session" severity="warning" :dismissible="false">{{ t('ui.publishedMenu') }}</BaseAlert>
          <AdminMenuManagement v-else :section="view === 'menu' ? 'editor' : 'revisions'" :items="menu" :categories="menuCategories" :revisions="menuRevisions" :sync-status="menuSyncStatus" :save-token="menuSavedToken" :busy="menuBusy" :can-manage="canManageMenu" :currency="currentProfile.currency" :language="adminLanguage" :dark="adminCore.theme.isDark.value" @save-draft="saveMenuDraft" @publish="publishMenu" @rollback="rollbackMenu" @open-revisions="selectView('menu-revisions')" @open-editor="selectView('menu')" @dirty-change="menuDraftDirty = $event" />
        </div>

        <div v-else-if="view === 'store-profile'" class="space-y-5">
          <div class="rounded-2xl border border-[var(--pn-border)] bg-[var(--pn-surface)] p-5 shadow-xl">
            <div class="flex flex-wrap items-start justify-between gap-3">
              <div><h2 class="font-semibold">{{ t('ui.storeProfile') }}</h2><p class="mt-1 text-sm text-[var(--pn-text-subtle)]">{{ t('ui.storeProfileDescription') }}</p></div>
              <div class="flex items-center gap-2"><BaseButton size="sm" variant="secondary" icon="History" @click="selectView('store-profile-history')">{{ t('ui.viewActivity') }}</BaseButton><div class="rounded-xl bg-[var(--pn-bg)] px-3 py-2 text-right text-xs text-[var(--pn-text-subtle)]"><div>{{ t('ui.storeCode') }} · {{ currentProfile.storeId }}</div></div></div>
            </div>
            <div class="mt-5 grid gap-4 md:grid-cols-2">
              <label class="text-sm text-[var(--pn-text-muted)] md:col-span-2">{{ t('ui.storeName') }}<BaseInput v-model="currentProfile.name" class="mt-1 w-full" /></label>
              <label class="text-sm text-[var(--pn-text-muted)] md:col-span-2">{{ t('ui.region') }}<BaseAutocomplete v-model="regionSearch" :options="regionAutocompleteOptions" item-key="id" display-key="name" :search-keys="['name', 'code', 'searchText']" :placeholder="t('ui.searchRegion')" class="mt-1 w-full" @select="selectRegion" /></label>
            </div>
          </div>

          <div class="rounded-2xl border border-[var(--pn-border)] bg-[var(--pn-surface)] p-5 shadow-xl">
            <h3 class="font-semibold">{{ t('ui.contactInformation') }}</h3>
            <div class="mt-4 grid gap-4 md:grid-cols-2">
              <label class="text-sm text-[var(--pn-text-muted)]">{{ t('ui.contactPhone') }}<BaseInput v-model="currentProfile.contactPhone" type="tel" class="mt-1 w-full" /></label>
              <label class="text-sm text-[var(--pn-text-muted)]">{{ t('ui.contactPerson') }}<BaseInput v-model="currentProfile.contactPerson" class="mt-1 w-full" /></label>
              <label class="text-sm text-[var(--pn-text-muted)] md:col-span-2">{{ t('ui.address') }}<BaseInput v-model="currentProfile.address" class="mt-1 w-full" /></label>
            </div>
          </div>

          <div class="rounded-2xl border border-[var(--pn-border)] bg-[var(--pn-surface)] p-5 shadow-xl">
            <h3 class="font-semibold">{{ t('ui.businessSettings') }}</h3>
            <div class="mt-4 grid gap-4 md:grid-cols-2">
              <label class="text-sm text-[var(--pn-text-muted)]">{{ t('ui.timezone') }}<BaseAutocomplete v-model="timezoneSearch" :options="timezoneAutocompleteOptions" item-key="id" display-key="name" :search-keys="['name', 'value', 'searchText']" :placeholder="t('ui.searchTimezone')" class="mt-1 w-full" @select="selectTimezone" /></label>
              <label class="text-sm text-[var(--pn-text-muted)]">{{ t('ui.currency') }}<PosSelect v-model="currentProfile.currency" :options="currencyOptions" class="mt-1 w-full" /></label>
              <label class="text-sm text-[var(--pn-text-muted)]">{{ t('ui.weekStarts') }}<PosSelect v-model="currentProfile.weekStartsOn" :options="weekStartOptions" class="mt-1 w-full" /></label>
              <label class="text-sm text-[var(--pn-text-muted)]">{{ t('ui.businessDayStartTime') }}<BaseDatePicker v-model="businessDayStartTime" mode="time" :adapter="posTimeAdapter" :time-zone="currentProfile.timeZone" locale="en-GB" display-format="HH:mm" :aria-label="t('ui.businessDayStartTime')" class="mt-1 w-full" /></label>
            </div>
            <div class="mt-6 flex justify-end"><BaseButton variant="primary" :disabled="busy" @click="saveProfile">{{ t('ui.saveServerProfile') }}</BaseButton></div>
          </div>
        </div>

        <div v-else-if="view === 'store-profile-history'" class="space-y-5">
          <div class="rounded-2xl border border-[var(--pn-border)] bg-[var(--pn-surface)] p-5 shadow-xl">
            <div class="flex items-start justify-between gap-3"><div><h2 class="font-semibold">{{ t('ui.storeActivity') }}</h2><p class="mt-1 text-sm text-[var(--pn-text-subtle)]">{{ t('ui.storeActivityNotice') }}</p></div><div class="flex items-center gap-2"><span class="text-xs text-[var(--pn-success)]">{{ identityAuditRows.length }} {{ t('ui.eventsCount') }}</span><BaseButton size="sm" variant="secondary" @click="selectView('store-profile')">{{ t('ui.backToStoreProfile') }}</BaseButton></div></div>
            <div v-if="identityAuditRows.length" class="mt-5 overflow-x-auto"><table class="w-full min-w-[40rem] text-left text-sm"><thead class="border-b border-[var(--pn-border)] text-xs uppercase tracking-wide text-[var(--pn-text-subtle)]"><tr><th class="px-3 py-3">{{ t('ui.event') }}</th><th class="px-3 py-3">{{ t('ui.actor') }}</th><th class="px-3 py-3">{{ t('ui.device') }}</th><th class="px-3 py-3">{{ t('ui.time') }}</th></tr></thead><tbody><tr v-for="row in identityAuditRows" :key="row.eventId" class="border-b border-[var(--pn-border)]"><td class="px-3 py-3"><BaseTag :label="row.eventType" severity="info" size="sm" /></td><td class="px-3 py-3">{{ row.actorId || t('ui.unavailable') }}</td><td class="px-3 py-3">{{ row.deviceId || t('ui.unavailable') }}</td><td class="whitespace-nowrap px-3 py-3">{{ formatDate(row.occurredAt) }}</td></tr></tbody></table></div>
            <p v-else class="mt-5 rounded-xl border border-dashed border-[var(--pn-border)] p-8 text-center text-sm text-[var(--pn-text-subtle)]">{{ t('ui.noStoreActivity') }}</p>
          </div>
        </div>


        <div v-else-if="view === 'management'" class="space-y-5">
          <BaseAlert v-if="!session" severity="warning" :dismissible="false">{{ t('ui.serverUsersNotice') }}</BaseAlert>
          <AdminUsersView v-else :users="managementUsers" :store-id="session.storeId" :busy="busy" :can-manage-users="canManageUsers" :language="adminLanguage" @create="createManagedUser" @update="updateManagedUser" @remove="removeManagedUser" />
        </div>

        <div v-else-if="view === 'devices' || view === 'device-history'" class="space-y-5">
          <BaseAlert v-if="!session" severity="warning" :dismissible="false">{{ t('ui.serverDevicesNotice') }}</BaseAlert>
          <AdminDevicesView v-else :devices="managementDevices" :health="managementDeviceHealth" :busy="busy" :can-manage="canManageDevices" :language="adminLanguage" :time-zone="currentProfile.timeZone" :history-open="deviceHistoryOpen || view === 'device-history'" :history-busy="deviceHistoryBusy" :history-device="deviceHistoryDevice" :history-rows="deviceHistoryRows" :history-page="deviceHistoryPage" :history-page-size="deviceHistoryPageSize" :history-total="deviceHistoryTotal" :bootstrap-source-ip="deviceHistoryBootstrapSourceIp" :bootstrap-user-agent="deviceHistoryBootstrapUserAgent" :security-metadata-visible="deviceHistorySecurityVisible" @update="updateManagedDevice" @toggle="toggleManagedDevice" @history="(device) => selectView('device-history', true, device.id)" @history-page="changeDeviceHistoryPage" @close-history="deviceHistoryOpen = false; selectView('devices')" />
        </div>

        <div v-if="view === 'conflicts'" class="space-y-5">
          <section class="rounded-2xl border border-[var(--pn-border)] bg-[var(--pn-surface)] p-5 text-[var(--pn-text)] shadow-xl">
            <div class="flex flex-wrap items-start justify-between gap-3"><div><div class="flex flex-wrap items-center gap-2"><h2 class="font-semibold">{{ t('ui.orderConflicts') }}</h2><span class="text-xs text-[var(--pn-success)]">{{ syncConflicts.length }} {{ t('ui.conflictsCount') }}</span><BaseTooltip :theme="adminTheme" :text="t('ui.orderConflictsHelp')" placement="bottom" /></div><p class="mt-1 text-sm text-[var(--pn-text-subtle)]">{{ t('ui.orderConflictsNotice') }}</p></div></div>
            <BaseAlert v-if="!session" class="mt-5" severity="warning" :dismissible="false">{{ t('ui.adminSignInRequired') }}</BaseAlert>
            <BaseAlert v-else-if="!canResolveConflicts" class="mt-5" severity="error" :dismissible="false">{{ t('ui.conflictPermissionRequired') }}</BaseAlert>
            <div v-else-if="syncConflicts.length === 0" class="mt-5 rounded-xl border border-dashed border-[var(--pn-border)] p-8 text-center text-sm text-[var(--pn-text-subtle)]">{{ t('ui.noOrderConflicts') }}</div>
            <div v-else class="mt-5 grid gap-3"><article v-for="conflict in syncConflicts" :key="conflict.id" class="rounded-xl border border-[var(--pn-border)] bg-[var(--pn-bg)] p-4"><div class="flex flex-wrap items-start justify-between gap-3"><div><h3 class="font-semibold">{{ t('ui.order') }} #{{ conflict.orderId }}</h3><p class="mt-1 text-xs text-[var(--pn-text-subtle)]">{{ conflict.reason }} · {{ formatDate(conflict.detectedAt) }}</p></div><BaseTag :label="conflict.status" severity="warning" size="sm" /></div><div class="mt-4 grid gap-3 lg:grid-cols-2"><div class="rounded-lg border border-[var(--pn-border)] bg-[var(--pn-surface)] p-3 text-sm"><div class="font-semibold text-[var(--pn-selection-text)]">{{ t('ui.localVersion') }}</div><div class="mt-2 text-[var(--pn-text-muted)]">{{ formatMoney(conflict.localOrder.totalMinor) }} · {{ conflict.localOrder.paymentMethod }}</div><div class="mt-1 break-all text-xs text-[var(--pn-text-subtle)]">SHA-256: {{ conflict.localPayloadHash }}</div></div><div class="rounded-lg border border-[var(--pn-border)] bg-[var(--pn-surface)] p-3 text-sm"><div class="font-semibold text-[var(--pn-success)]">{{ t('ui.serverVersion') }}</div><div class="mt-2 text-[var(--pn-text-muted)]">{{ formatMoney(conflict.remoteOrder.totalMinor) }} · {{ conflict.remoteOrder.paymentMethod }}</div><div class="mt-1 break-all text-xs text-[var(--pn-text-subtle)]">SHA-256: {{ conflict.remotePayloadHash }}</div></div></div><div class="mt-4 flex flex-wrap justify-end gap-2"><BaseButton variant="secondary" size="sm" :disabled="busy" @click="resolveConflict(conflict, 'keep-server')">{{ t('ui.keepServer') }}</BaseButton><BaseButton variant="secondary" size="sm" :disabled="busy" @click="resolveConflict(conflict, 'accept-local-as-correction')">{{ t('ui.acceptLocalCorrection') }}</BaseButton><BaseButton variant="danger" size="sm" :disabled="busy" @click="resolveConflict(conflict, 'reject-local')">{{ t('ui.rejectLocal') }}</BaseButton></div></article></div>
          </section>
        </div>
    <BaseDialog
      v-model:visible="orderDialogOpen"
      :dialog-label="`${t('ui.orderNumber')} #${selectedOrder ? displayOrderNumber(selectedOrder) : ''}`"
      max-width="2xl"
      :closable="true"
      :close-on-escape="true"
      :dismissable-mask="true"
      :draggable="false"
      mobile-style="sheet"
      @update:visible="(visible) => { if (!visible) closeOrderDialog(); }"
    >
      <template #header>
        <div v-if="selectedOrder" class="grid min-w-0 gap-1">
          <strong class="truncate text-base font-semibold">{{ t('ui.orderNumber') }} #{{ displayOrderNumber(selectedOrder) }}</strong>
          <div class="flex flex-wrap items-center gap-3 text-sm text-[var(--pn-text-subtle)]">
            <span class="inline-flex items-center gap-1.5"><BaseIcon name="History" :size="14" decorative />{{ formatDate(selectedOrder.createdAt) }}</span>
            <span class="inline-flex items-center gap-1.5"><BaseIcon name="UserCircle" :size="14" decorative />{{ creatorLabel(selectedOrder.actorId) }}</span>
          </div>
        </div>
      </template>

      <div v-if="selectedOrder" class="space-y-4">
        <div class="space-y-3">
          <div v-for="(line, lineIndex) in selectedOrder.lines" :key="`${line.productId}-${lineIndex}-${line.note ?? ''}`" class="flex justify-between gap-4 rounded-xl bg-[var(--pn-bg)] p-3 text-sm">
            <span class="min-w-0">
              <span>{{ line.quantity }} × {{ line.name }}</span>
              <small v-if="line.options?.length" class="mt-1 block text-xs text-[var(--pn-text-subtle)]">{{ line.options.map((option) => option.name).join(' · ') }}</small>
              <small v-if="line.note" class="mt-1 block text-xs text-[var(--pn-text-subtle)]">{{ t('ui.itemNote') }}: {{ line.note }}</small>
            </span>
            <span class="shrink-0">{{ formatMoney(orderLineAmount(selectedOrder, line)) }}</span>
          </div>
        </div>

        <div class="border-t border-[var(--pn-border)] pt-5">
          <div class="ml-auto w-2/5 max-[640px]:w-full">
            <div class="grid gap-4 text-sm">
              <div class="flex items-center justify-between gap-4"><span class="text-[var(--pn-text-subtle)]">{{ t('ui.subtotal') }}</span><span>{{ formatMoney(selectedOrder.subtotalMinor) }}</span></div>
              <div v-if="selectedOrder.discountMinor > 0" class="flex items-center justify-between gap-4 text-[var(--pn-warning)]"><span>{{ t('ui.discount') }}<span v-if="discountLabel(selectedOrder)" class="ml-1 text-xs">({{ discountLabel(selectedOrder) }})</span></span><span>-{{ formatMoney(selectedOrder.discountMinor) }}</span></div>
              <div class="flex items-center justify-between gap-4 border-t border-[var(--pn-border)] pt-4 text-base font-semibold"><span>{{ t('ui.total') }}</span><span>{{ formatMoney(selectedOrder.totalMinor) }}</span></div>
            </div>

            <BaseAccordion
              class="mt-5"
              :model-value="orderPaymentDetailsOpen"
              @update:model-value="orderPaymentDetailsOpen = $event"
            >
              <template #title>
                <span class="text-[var(--pn-text-subtle)]">{{ t('ui.payment') }}</span>
              </template>
              <template #actions><BaseTag :label="t(paymentTag(selectedOrder.paymentMethod).label)" :icon="paymentTag(selectedOrder.paymentMethod).icon" :severity="paymentTag(selectedOrder.paymentMethod).severity" rounded size="sm" /></template>
              <div v-if="selectedOrder.payment" class="mt-3 grid gap-3 rounded-xl bg-[var(--pn-bg)] p-4 text-sm">
                <div v-if="selectedOrder.paymentMethod === 'cash' || selectedOrder.paymentMethod === 'mixed'" class="flex items-center justify-between gap-4"><span class="text-[var(--pn-text-subtle)]">{{ t('ui.cashDue') }}</span><span>{{ formatMoney(selectedOrder.payment.cashMinor) }}</span></div>
                <div v-if="selectedOrder.paymentMethod === 'card' || selectedOrder.paymentMethod === 'mixed'" class="flex items-center justify-between gap-4"><span class="text-[var(--pn-text-subtle)]">{{ t('ui.card') }}</span><span>{{ formatMoney(selectedOrder.payment.cardMinor) }}</span></div>
                <div v-if="selectedOrder.payment.cashReceivedMinor !== undefined" class="flex items-center justify-between gap-4"><span class="text-[var(--pn-text-subtle)]">{{ t('ui.received') }}</span><span>{{ formatMoney(selectedOrder.payment.cashReceivedMinor) }}</span></div>
                <div v-if="cashChangeMinor(selectedOrder) !== null" class="flex items-center justify-between gap-4 text-[var(--pn-success)]"><span>{{ t('ui.change') }}</span><span>{{ formatMoney(cashChangeMinor(selectedOrder) ?? 0) }}</span></div>
              </div>
              <p v-else class="mt-3 rounded-xl bg-[var(--pn-bg)] p-4 text-sm text-[var(--pn-text-subtle)]">{{ t('ui.unavailable') }}</p>
            </BaseAccordion>
          </div>
        </div>

        <div v-if="selectedOrderEvents.length" class="border-t border-[var(--pn-border)] pt-4">
          <h3 class="font-semibold">{{ t('ui.correctionHistory') }}</h3>
          <div class="mt-3 space-y-2">
            <div v-for="event in selectedOrderEvents" :key="event.id" class="rounded-xl bg-[var(--pn-bg)] p-3 text-sm">
              <div class="flex justify-between gap-3"><span>{{ event.label }}</span><span class="text-xs text-[var(--pn-text-subtle)]">{{ formatDate(event.occurredAt) }}</span></div>
              <div class="mt-1 text-xs text-[var(--pn-text-subtle)]">{{ event.actorId }}<span v-if="event.reason"> · {{ event.reason }}</span></div>
            </div>
          </div>
        </div>
      </div>

      <template #footer>
        <BaseButton v-if="selectedOrder?.status === 'completed'" variant="danger" icon="Trash2" @click="voidOrderFromDetail">{{ t('ui.void') }}</BaseButton>
      </template>
    </BaseDialog>
    </AdminShell>
    </template>
    <BaseDialog
      v-model:visible="storeSwitchDialogOpen"
      :header="t('ui.switchStore')"
      :dialog-label="t('ui.switchStore')"
      max-width="sm"
      :closable="!storeSwitchBusy"
      :close-on-escape="!storeSwitchBusy"
      :dismissable-mask="!storeSwitchBusy"
      :draggable="false"
    >
      <div class="space-y-4">
        <div class="flex items-start gap-3 rounded-xl border border-[var(--pn-border)] bg-[var(--pn-surface-muted)] p-3">
          <span class="grid size-7 shrink-0 place-items-center rounded-[.55rem] bg-[color-mix(in_srgb,var(--pn-brand)_12%,var(--pn-surface))] text-[var(--pn-brand)]" aria-hidden="true"><BaseIcon name="Store" :size="16" decorative /></span>
          <p class="text-[var(--pn-text-muted)] text-sm leading-6">{{ t('ui.switchStoreHelp') }}</p>
        </div>
        <label class="text-[var(--pn-text-muted)] block text-sm">{{ t('ui.store') }}
          <BaseSelect v-model="pendingStoreId" class="mt-1 w-full" :options="storeOptions" :disabled="storeSwitchBusy" :aria-label="t('ui.switchStore')" />
        </label>
        <BaseAlert v-if="authError" severity="warning" @close="authError = ''">{{ authError }}</BaseAlert>
      </div>
      <template #footer>
        <BaseButton variant="secondary" :disabled="storeSwitchBusy" @click="storeSwitchDialogOpen = false">{{ t('ui.cancel') }}</BaseButton>
        <BaseButton variant="primary" :loading="storeSwitchBusy" :disabled="storeSwitchBusy || !pendingStoreId || pendingStoreId === session?.storeId" @click="void confirmStoreSwitch()">{{ t('ui.switchStore') }}</BaseButton>
      </template>
    </BaseDialog>
    <PwaUpdateDialog
      v-model:visible="pwaUpdateVisible"
      :busy="pwaUpdateBusy"
      :title="t('ui.updateAvailable')"
      :message="t('ui.updateMessage')"
      :later-label="t('ui.updateLater')"
      :update-label="t('ui.updateNow')"
      @update-now="applyPwaUpdate"
    />
    <ConfirmationDialog />
  </div>
</template>
